<template>
    <mobile-app-shell
        title="우리집 에너지"
        :show-logout="false"
        :tabs="shellTabs"
        :active-tab="activeTab"
        @tab-change="setActiveTab"
    >
        <div v-if="loadingMe" class="state-box empty-state">
            <b-spinner variant="primary" />
            <p class="empty-title">세대 정보를 불러오는 중</p>
        </div>
        <div v-else-if="meError" class="state-box error empty-state">
            <i class="fa fa-exclamation-circle fa-2x"></i>
            <p class="empty-title">세대를 확인할 수 없어요</p>
            <p class="empty-desc">{{ meError }}</p>
        </div>
        <template v-else>
            <!-- 월 선택 bottom sheet — 요금·이력에서 공유 -->
            <template v-if="monthSheetOpen">
                <div class="sheet-backdrop" @click="closeMonthSheet"></div>
                <div class="sheet-panel open">
                    <div class="sheet-handle"></div>
                    <div class="sheet-title">청구 월 선택</div>
                    <div v-if="loadingMonthOptions" class="state-box small">
                        <b-spinner small variant="primary" />
                    </div>
                    <div v-else-if="!monthOptions.length" class="state-box small empty-state">
                        <i class="fa fa-calendar-times"></i>
                        <p class="empty-title">선택 가능한 청구 월이 없어요</p>
                    </div>
                    <div v-else class="sheet-list">
                        <button
                            v-for="opt in monthOptions"
                            :key="opt.regYm"
                            type="button"
                            class="sheet-item"
                            :class="{ active: regYm === opt.regYm }"
                            @click="selectMonth(opt.regYm)"
                        >
                            <div class="sheet-item-main">
                                <span class="sheet-item-ym">{{ fmtYmBillLabel(opt.regYm) }}</span>
                                <span v-if="monthOptionPeriodText(opt)" class="sheet-item-period">{{
                                    monthOptionPeriodText(opt)
                                }}</span>
                            </div>
                            <span v-if="opt.finalized" class="sheet-item-amt num"
                                >{{ formatWon(opt.totalAmount) }}원</span
                            >
                            <span v-else class="sheet-item-pending">{{ monthOptionBadge(opt) }}</span>
                            <i v-if="regYm === opt.regYm" class="fa fa-check sheet-item-check"></i>
                        </button>
                    </div>
                </div>
            </template>

            <!-- 요금·인사이트 공용: 세대·청구월·에너지원 (이력/더보기에서는 숨김) -->
            <div v-if="showEnergyContext" class="ctx-bar">
                <section class="hh-row">
                    <div>
                        <div class="hh-name">{{ me.zoneName ?? "-" }}</div>
                    </div>
                    <button type="button" class="hh-month" @click="openMonthSheet">
                        <span class="hh-month-label">{{ headerMonthLabel }}</span>
                        <i class="fa fa-chevron-down hh-month-icon"></i>
                    </button>
                </section>
                <div v-if="energyList.length" class="seg-row seg-row-ctx">
                    <button
                        v-for="e in energyList"
                        :key="'ctx-' + e.key"
                        type="button"
                        class="seg-btn"
                        :class="{ active: energy === e.key }"
                        @click="setEnergy(e.key)"
                    >
                        {{ e.label }}
                    </button>
                </div>
            </div>

            <!-- ========== 탭1: 요금 ========== -->
            <div v-show="activeTab === 'bill'">
                <section v-if="energySourcesOnly.length" class="card">
                    <div class="sec-head">
                        <h2 class="sec-title">{{ fmtYmLabel(regYm) }} 청구 요약</h2>
                    </div>
                    <div class="sum-table">
                        <div v-for="r in billingSummaryRows" :key="r.key" class="sum-row">
                            <span class="sum-name">{{ r.label }}</span>
                            <span class="sum-usage num">{{ r.usageText }}</span>
                            <span v-if="r.available" class="sum-amt num">{{ formatWon(r.amount) }}원</span>
                            <span v-else-if="r.hasEstimateAmount" class="sum-amt num is-forecast"
                                >약 {{ formatWon(r.amountForecast) }}원</span
                            >
                            <span v-else class="sum-amt is-pending">확정 전</span>
                        </div>
                    </div>
                    <div v-if="showTotalAmount" class="sum-total">
                        <span class="sum-total-label">{{ totalAmountSummaryLabel }}</span>
                        <span class="sum-total-val num"
                            >{{ totalAmountIsEstimate ? "약 " : "" }}{{ formatWon(displayTotalAmount)
                            }}<small>원</small></span
                        >
                    </div>
                    <div v-else class="empty-state inline">
                        <i class="fa fa-file-invoice"></i>
                        <p class="empty-title">아직 이번 달 사용 데이터가 없어요</p>
                        <p class="empty-desc">검침이 쌓이면 추정 요금과 확정 청구가 여기에 표시됩니다.</p>
                    </div>
                    <p
                        v-if="totalAmountIsEstimate"
                        class="forecast-note"
                        :class="{ 'is-shock': hasShockCaution }"
                    >
                        <i class="fa" :class="hasShockCaution ? 'fa-exclamation-triangle' : 'fa-info-circle'"></i>
                        {{ forecastDisclaimer }}
                    </p>
                </section>

                <section class="card">
                    <div class="sec-head">
                        <h2 class="sec-title">{{ fmtYmLabel(regYm) }} 상세 청구 내역</h2>
                        <div
                            v-if="prevAmountPct !== null"
                            class="hero-delta"
                            :class="prevAmountPct > 0 ? 'is-up' : 'is-down'"
                        >
                            {{ prevAmountPct > 0 ? "▲" : "▼" }} 전월
                            {{ pctText(prevAmountPct) }}
                        </div>
                    </div>

                    <!-- 전체: 에너지원별 나열 -->
                    <div v-if="isAllEnergy" class="all-energy-list">
                        <div v-for="r in allEnergyInsightRows" :key="'bill-all-' + r.key" class="all-energy-row">
                            <span class="all-en-name">{{ r.label }}</span>
                            <span class="all-en-usage num">{{ r.usageText }}</span>
                            <span
                                class="all-en-amt num"
                                :class="{ 'is-forecast': r.hasEstimateAmount && !r.available }"
                                >{{ r.amountText }}</span
                            >
                        </div>
                        <p v-if="showEstimatePanel" class="forecast-note">
                            <i class="fa fa-info-circle"></i>
                            {{ forecastDisclaimer }}
                        </p>
                    </div>

                    <template v-else-if="curBilling && curBilling.billingAvailable">
                        <div v-if="billingMetaText" class="hero-meta">
                            <i class="fa fa-calendar-alt"></i>
                            <span>{{ billingMetaText }}</span>
                        </div>
                        <div class="bill-lines">
                            <div class="bill-line is-major">
                                <span class="bill-key">청구 금액</span>
                                <span class="bill-val num">{{ formatWon(displayDetailAmount) }}<small>원</small></span>
                            </div>
                            <div class="bill-line">
                                <span class="bill-key">사용량</span>
                                <span class="bill-val num"
                                    >{{ formatUsage(curBilling.usageQty) }} <small>{{ currentUnit }}</small></span
                                >
                            </div>
                        </div>
                        <div v-if="showReceipt" class="hero-receipt">
                            <h3 class="blk-title">산출 내역</h3>
                            <div class="detail-row">
                                <span>개별 사용요금</span>
                                <span class="num">{{ formatWon(curBilling.individualCost) }}원</span>
                            </div>
                            <div class="detail-row">
                                <span>공동설비 배분</span>
                                <span class="num">{{ formatWon(curBilling.allocatedCommonCost) }}원</span>
                            </div>
                            <div v-if="hasManualAdjust" class="detail-row">
                                <span>수동 조정</span>
                                <span class="num" :class="manualAdjustClass">{{ manualAdjustSigned }}원</span>
                            </div>
                            <div class="detail-row detail-total">
                                <span>합계</span>
                                <span class="num">{{ formatWon(curBilling.totalAmount) }}원</span>
                            </div>
                        </div>
                    </template>
                    <template v-else>
                        <div class="bill-lines">
                            <div class="bill-line is-major">
                                <span class="bill-key">{{ pendingAmountLabel }}</span>
                                <span v-if="curForecastHasEstimate" class="bill-val num is-forecast"
                                    >약 {{ formatWon(curForecast.amountForecast) }}<small>원</small></span
                                >
                                <span v-else class="bill-val is-pending">확정 전</span>
                            </div>
                            <div class="bill-line">
                                <span class="bill-key">{{ pendingUsageLabel }}</span>
                                <span class="bill-val num"
                                    >{{ formatUsage(curUsageQty) }} <small>{{ currentUnit }}</small></span
                                >
                            </div>
                        </div>
                        <div class="hero-meta">
                            <i class="fa fa-info-circle"></i>
                            <span>{{ pendingBillingHint }}</span>
                        </div>
                    </template>
                </section>
            </div>

            <!-- ========== 탭: 인사이트 ========== -->
            <div v-show="activeTab === 'insight'">
                <!-- 전체 탭: 에너지원별 요약 (ALL) -->
                <section v-if="isAllEnergy" class="card">
                    <div class="sec-head">
                        <h2 class="sec-title">{{ fmtYmLabel(regYm) }} 에너지원별 현황</h2>
                    </div>
                    <div class="all-energy-list">
                        <div v-for="r in allEnergyInsightRows" :key="'ins-all-' + r.key" class="all-energy-row">
                            <span class="all-en-name">{{ r.label }}</span>
                            <span class="all-en-usage num">{{ r.usageText }}</span>
                            <span
                                class="all-en-amt num"
                                :class="{ 'is-forecast': r.hasEstimateAmount && !r.available }"
                                >{{ r.amountText }}</span
                            >
                        </div>
                    </div>
                </section>

                <!-- 청구 전 추정 / 검침 중 예상 -->
                <section
                    v-if="showEstimatePanel"
                    class="card forecast-card"
                    :class="{ 'is-shock': hasShockCaution }"
                >
                    <div class="sec-head">
                        <h2 class="sec-title">{{ fmtYmLabel(regYm) }} {{ estimatePanelTitle }}</h2>
                    </div>
                    <div class="forecast-hero">
                        <div class="forecast-amt num">
                            약 {{ formatWon(isAllEnergy ? forecastAmountTotal : curForecast?.amountForecast)
                            }}<small>원</small>
                        </div>
                        <p class="forecast-note" :class="{ 'is-shock': hasShockCaution }">
                            <i
                                class="fa"
                                :class="hasShockCaution ? 'fa-exclamation-triangle' : 'fa-info-circle'"
                            ></i>
                            {{ forecastDisclaimer }}
                        </p>
                    </div>
                    <div v-if="!isAllEnergy && isMeasuredPendingStatus(curForecast)" class="forecast-meta num">
                        실측 {{ formatUsage(curForecast.usageMtd) }}
                        {{ curForecast.usageUnit || currentUnit }}
                    </div>
                    <div v-else-if="!isAllEnergy && isInProgressForecastStatus(curForecast)" class="forecast-meta num">
                        현재까지 {{ formatUsage(curForecast.usageMtd) }} {{ curForecast.usageUnit || currentUnit }} ·
                        예상
                        {{ formatUsage(curForecast.usageForecast) }}
                        {{ curForecast.usageUnit || currentUnit }}
                    </div>
                </section>
                <section
                    v-else-if="!isAllEnergy && curForecast && curForecast.status === 'insufficient_data'"
                    class="card"
                >
                    <div class="state-box small empty-state">
                        <i class="fa fa-hourglass-half"></i>
                        <p class="empty-title">아직 이번 달 사용 데이터가 없어요</p>
                        <p class="empty-desc">검침이 며칠 쌓이면 추정·예상 요금을 보여드려요.</p>
                    </div>
                </section>

                <!-- 전체: 요금(원) 월별 트레일 분석 — 일별·사용량 단위 혼합 없음 -->
                <section v-if="isAllEnergy" class="card">
                    <div class="sec-head">
                        <h2 class="sec-title">{{ fmtYmLabel(regYm) }} 요금 분석</h2>
                    </div>
                    <p v-if="amountPeriodText && !usagePending && !showUsageSkeleton" class="usage-period num">
                        {{ amountPeriodText }}
                    </p>
                    <div v-if="showUsageSkeleton" class="ins-skel" aria-busy="true" aria-hidden="true">
                        <div class="ins-skel-msg">
                            <span class="ins-skel-icon skel-shimmer"></span>
                            <div class="ins-skel-text">
                                <span class="ins-skel-line skel-shimmer w-72"></span>
                                <span class="ins-skel-line skel-shimmer w-90"></span>
                            </div>
                        </div>
                        <div class="ins-skel-gauge">
                            <span class="ins-skel-line skel-shimmer w-100 h-8"></span>
                            <span class="ins-skel-line skel-shimmer w-40 center"></span>
                        </div>
                        <div class="ins-skel-list">
                            <div v-for="n in 3" :key="'ask-' + n" class="ins-skel-row">
                                <span class="ins-skel-line skel-shimmer w-36"></span>
                                <span class="ins-skel-line skel-shimmer w-44"></span>
                            </div>
                        </div>
                    </div>
                    <div v-else-if="usagePending" class="ins-skel-slot" aria-busy="true"></div>
                    <div v-else-if="!amountInsightSummary" class="state-box small empty-state">
                        <i class="fa fa-puzzle-piece"></i>
                        <p class="empty-title">분석할 요금이 아직 부족해요</p>
                        <p class="empty-desc">전 에너지원 청구가 확정되면 요금 패턴을 보여드려요.</p>
                    </div>
                    <div v-else class="ins-wrap">
                        <div class="ins-msg" :class="amountInsightSummary.tone">
                            <i class="ins-icon" :class="[amountInsightSummary.icon, amountInsightSummary.tone]"></i>
                            <div class="ins-text">
                                <strong>{{ amountInsightSummary.title }}</strong>
                                <p>{{ amountInsightSummary.desc }}</p>
                            </div>
                        </div>

                        <div v-if="amountInsightSummary.comparable" class="ins-gauge">
                            <div class="ins-gauge-labels">
                                <span class="z-save">적게</span>
                                <span class="z-mid">평균</span>
                                <span class="z-over">많이</span>
                            </div>
                            <div class="ins-gauge-track">
                                <div
                                    class="ins-gauge-pin"
                                    :class="amountInsightSummary.tone"
                                    :style="{ left: amountInsightSummary.gaugePercent + '%' }"
                                ></div>
                            </div>
                            <div class="ins-gauge-caption num">
                                {{ amountInsightSummary.ratioText }}
                            </div>
                        </div>

                        <div v-if="amountInsightSummary.count >= 2" class="ins-list">
                            <div class="ins-row">
                                <span class="ins-label">가장 많이 낸 달</span>
                                <div class="ins-value-stack">
                                    <span class="ins-value num is-over">{{ amountInsightSummary.max.label }}</span>
                                    <span class="ins-value-sub num">{{ formatWon(amountInsightSummary.max.v) }}원</span>
                                </div>
                            </div>
                            <div class="ins-row">
                                <span class="ins-label">가장 적게 낸 달</span>
                                <div class="ins-value-stack">
                                    <span class="ins-value num is-save">{{ amountInsightSummary.min.label }}</span>
                                    <span class="ins-value-sub num">{{ formatWon(amountInsightSummary.min.v) }}원</span>
                                </div>
                            </div>
                            <div class="ins-row">
                                <span class="ins-label">선택 기간 평균</span>
                                <span class="ins-value num">{{ formatWon(amountInsightSummary.avg) }}원</span>
                            </div>
                        </div>
                    </div>
                    <div v-if="amountDroppedNote && !usagePending && !showUsageSkeleton" class="ins-foot">
                        <p class="ins-note">
                            <i class="fa fa-info-circle"></i>
                            <span>{{ amountDroppedNote }}</span>
                        </p>
                    </div>
                </section>

                <section v-if="!isAllEnergy" class="card">
                    <div class="sec-head">
                        <h2 class="sec-title">{{ fmtYmLabel(regYm) }} 사용 분석</h2>
                        <div class="period-toggle">
                            <button type="button" :class="{ active: period === 'month' }" @click="setPeriod('month')">
                                월별
                            </button>
                            <button type="button" :class="{ active: period === 'day' }" @click="setPeriod('day')">
                                일별
                            </button>
                        </div>
                    </div>
                    <p v-if="usagePeriodText && !usagePending && !showUsageSkeleton" class="usage-period num">
                        {{ usagePeriodText }}
                    </p>
                    <div v-if="showUsageSkeleton" class="ins-skel" aria-busy="true" aria-hidden="true">
                        <div class="ins-skel-msg">
                            <span class="ins-skel-icon skel-shimmer"></span>
                            <div class="ins-skel-text">
                                <span class="ins-skel-line skel-shimmer w-72"></span>
                                <span class="ins-skel-line skel-shimmer w-90"></span>
                            </div>
                        </div>
                        <div class="ins-skel-gauge">
                            <span class="ins-skel-line skel-shimmer w-100 h-8"></span>
                            <span class="ins-skel-line skel-shimmer w-40 center"></span>
                        </div>
                        <div class="ins-skel-list">
                            <div v-for="n in 3" :key="'usk-' + n" class="ins-skel-row">
                                <span class="ins-skel-line skel-shimmer w-36"></span>
                                <span class="ins-skel-line skel-shimmer w-44"></span>
                            </div>
                        </div>
                    </div>
                    <div v-else-if="usagePending" class="ins-skel-slot" aria-busy="true"></div>
                    <div v-else-if="usageEmpty" class="state-box small empty-state">
                        <i class="fa fa-chart-area"></i>
                        <p class="empty-title">이 기간의 사용 데이터가 없어요</p>
                        <p class="empty-desc">다른 월이나 에너지원을 선택해 보세요.</p>
                    </div>
                    <div v-else-if="!insightSummary" class="state-box small empty-state">
                        <i class="fa fa-puzzle-piece"></i>
                        <p class="empty-title">분석할 데이터가 아직 부족해요</p>
                        <p class="empty-desc">검침이 쌓이면 평균 대비 분석을 보여드려요.</p>
                    </div>
                    <div v-else class="ins-wrap">
                        <div class="ins-msg" :class="insightSummary.tone">
                            <i class="ins-icon" :class="[insightSummary.icon, insightSummary.tone]"></i>
                            <div class="ins-text">
                                <strong>{{ insightSummary.title }}</strong>
                                <p>{{ insightSummary.desc }}</p>
                            </div>
                        </div>

                        <div v-if="insightSummary.comparable" class="ins-gauge">
                            <div class="ins-gauge-labels">
                                <span class="z-save">적게</span>
                                <span class="z-mid">평균</span>
                                <span class="z-over">많이</span>
                            </div>
                            <div class="ins-gauge-track">
                                <div
                                    class="ins-gauge-pin"
                                    :class="insightSummary.tone"
                                    :style="{ left: insightSummary.gaugePercent + '%' }"
                                ></div>
                            </div>
                            <div class="ins-gauge-caption num">
                                {{ insightSummary.ratioText }}
                            </div>
                        </div>

                        <div v-if="insightSummary.count >= 2" class="ins-list">
                            <div class="ins-row">
                                <span class="ins-label">가장 많이 쓴 {{ periodUnitLabel }}</span>
                                <div class="ins-value-stack">
                                    <span class="ins-value num is-over">{{ insightSummary.max.label }}</span>
                                    <span class="ins-value-sub num"
                                        >{{ formatUsage(insightSummary.max.v) }} {{ currentUnit }}</span
                                    >
                                </div>
                            </div>
                            <div class="ins-row">
                                <span class="ins-label">가장 적게 쓴 {{ periodUnitLabel }}</span>
                                <div class="ins-value-stack">
                                    <span class="ins-value num is-save">{{ insightSummary.min.label }}</span>
                                    <span class="ins-value-sub num"
                                        >{{ formatUsage(insightSummary.min.v) }} {{ currentUnit }}</span
                                    >
                                </div>
                            </div>
                            <div class="ins-row">
                                <span class="ins-label">선택 기간 평균</span>
                                <span class="ins-value num"
                                    >{{ formatUsage(insightSummary.avg) }} {{ currentUnit }}</span
                                >
                            </div>
                        </div>
                    </div>
                    <div v-if="usageDroppedNote && !usagePending && !showUsageSkeleton" class="ins-foot">
                        <p class="ins-note">
                            <i class="fa fa-info-circle"></i>
                            <span>{{ usageDroppedNote }}</span>
                        </p>
                    </div>
                </section>

                <section v-if="showCompareSection" class="card">
                    <div class="sec-head">
                        <h2 class="sec-title">{{ fmtYmLabel(regYm) }} 비교 분석</h2>
                    </div>
                    <div v-if="!compareShownRows.length && !compareInsight" class="state-box small empty-state">
                        <i class="fa fa-users"></i>
                        <p class="empty-title">비교할 기준 데이터가 없어요</p>
                        <p class="empty-desc">전월·전년·유사 면적 청구가 확정되면 보여드려요.</p>
                    </div>
                    <template v-else>
                        <div v-if="compareInsight" class="cmp-insight" :class="compareInsight.tone">
                            <i :class="compareInsight.icon"></i>
                            <div class="cmp-insight-text">
                                <strong>
                                    <span class="cmp-key">{{ compareInsight.key }}</span> {{ compareInsight.tail }}
                                </strong>
                                <p>{{ compareInsight.sub }}</p>
                            </div>
                        </div>
                        <div v-for="(row, i) in compareShownRows" :key="i" class="cmp-block">
                            <div class="grp-head">
                                <h3 class="blk-title">{{ row.label }}</h3>
                                <span v-if="row.meta" class="grp-meta">{{ row.meta }}</span>
                            </div>
                            <div class="ledger-row ledger-head">
                                <span></span>
                                <span class="r">{{ row.refColLabel }}</span>
                                <span class="delta">{{ row.neutral ? "차이" : "변화" }}</span>
                            </div>
                            <div v-if="row.usage" class="ledger-row ledger-data">
                                <span class="metric-label">사용량</span>
                                <span class="r num v-base">{{ formatUsage(row.usage.ref) }} {{ row.usage.unit }}</span>
                                <span class="delta num v-delta" :class="deltaTone(row.usage.pct)">
                                    {{ deltaPctSymbol(row.usage.pct) }}
                                </span>
                            </div>
                            <div v-if="row.amount" class="ledger-row ledger-data ledger-gap">
                                <span class="metric-label">요금</span>
                                <span class="r num v-base">{{ formatWon(row.amount.ref) }}{{ row.amount.unit }}</span>
                                <span class="delta num v-delta" :class="deltaTone(row.amount.pct)">
                                    {{ deltaPctSymbol(row.amount.pct) }}
                                </span>
                            </div>
                        </div>
                        <div v-if="compareAmountPendingText || compareMissingText" class="cmp-foot">
                            <i class="fa fa-info-circle"></i>
                            <div class="cmp-foot-msgs">
                                <p v-if="compareAmountPendingText">
                                    {{ compareAmountPendingText }}
                                </p>
                                <p v-if="compareMissingText">{{ compareMissingText }}</p>
                            </div>
                        </div>
                    </template>
                </section>
            </div>

            <!-- ========== 탭3: 청구 이력 ========== -->
            <div v-show="activeTab === 'history'">
                <section class="hh-row">
                    <div>
                        <div class="hh-name">청구 이력</div>
                        <div class="hh-sub">확정된 청구만 표시</div>
                    </div>
                </section>

                <div v-if="loadingHistory" class="state-box">
                    <b-spinner variant="primary" />
                    <p class="empty-title">이력을 불러오는 중</p>
                </div>
                <div v-else-if="historyError" class="state-box error empty-state">
                    <i class="fa fa-exclamation-circle fa-2x"></i>
                    <p class="empty-title">이력을 불러오지 못했어요</p>
                    <p class="empty-desc">{{ historyError }}</p>
                    <button type="button" class="retry-btn" @click="loadHistory(true)">다시 시도</button>
                </div>
                <div v-else-if="!historyMonths.length" class="state-box empty-state">
                    <i class="fa fa-inbox fa-2x"></i>
                    <p class="empty-title">확정된 청구가 아직 없어요</p>
                    <p class="empty-desc">관리사무소에서 정산이 확정되면 여기에 쌓여요.</p>
                </div>
                <div v-else class="hist-list">
                    <div
                        v-for="m in historyMonths"
                        :key="m.regYm"
                        class="hist-card"
                        :class="{ open: isHistoryOpen(m.regYm) }"
                    >
                        <button type="button" class="hist-toggle" @click="toggleHistoryMonth(m.regYm)">
                            <div class="hist-main">
                                <div class="hist-ym">{{ fmtYmBillLabel(m.regYm) }}</div>
                                <div class="hist-amt num">{{ formatWon(m.totalAmount) }}원</div>
                                <i
                                    class="fa hist-chevron"
                                    :class="isHistoryOpen(m.regYm) ? 'fa-chevron-up' : 'fa-chevron-down'"
                                    aria-hidden="true"
                                ></i>
                            </div>
                            <div v-if="m.yoyPct !== null" class="hist-meta">
                                <span class="hist-yoy" :class="deltaTone(m.yoyPct)">
                                    작년 동월 {{ deltaPctSymbol(m.yoyPct) }}
                                </span>
                            </div>
                        </button>
                        <div v-if="isHistoryOpen(m.regYm)" class="hist-detail">
                            <div v-for="row in m.byEnergy" :key="m.regYm + row.energySource" class="hist-energy">
                                <span class="hist-en-name">{{ energyLabel(row.energySource) }}</span>
                                <span class="hist-en-usage num"
                                    >{{ formatUsage(row.usageQty) }}{{ row.usageUnit ? ` ${row.usageUnit}` : "" }}</span
                                >
                                <span class="hist-en-amt num">{{ formatWon(row.totalAmount) }}원</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- ========== 탭4: 더보기 ========== -->
            <div v-show="activeTab === 'more'">
                <section class="card">
                    <div class="sec-head">
                        <h2 class="sec-title">내 정보</h2>
                    </div>
                    <div class="more-rows">
                        <div class="more-row">
                            <span class="more-key">세대</span>
                            <span class="more-val">{{ me.zoneName || displayZoneName || "-" }}</span>
                        </div>
                        <div v-if="me.roomName" class="more-row">
                            <span class="more-key">호실</span>
                            <span class="more-val">{{ me.roomName }}</span>
                        </div>
                        <div v-if="me.exclusiveArea != null" class="more-row">
                            <span class="more-key">전용면적</span>
                            <span class="more-val num">{{ formatUsage(me.exclusiveArea) }}㎡</span>
                        </div>
                        <div class="more-row">
                            <span class="more-key">에너지원</span>
                            <span class="more-val">{{ energySourcesOnly.map((e) => e.label).join(" · ") || "-" }}</span>
                        </div>
                    </div>
                </section>
                <section class="card more-actions">
                    <button type="button" class="more-action" @click="pwdSheetOpen = true">
                        <i class="fa fa-key"></i>
                        비밀번호 변경
                    </button>
                    <button type="button" class="more-action danger" @click="logout">
                        <i class="fa fa-sign-out-alt"></i>
                        로그아웃
                    </button>
                </section>
            </div>

            <hems-change-password-sheet
                :open="pwdSheetOpen"
                :user-id="loginUserId"
                @close="pwdSheetOpen = false"
            />
        </template>
    </mobile-app-shell>
</template>

<script>
import backEndApi from "@api/backEndApi.js";
import MobileAppShell from "./MobileAppShell.vue";
import HemsChangePasswordSheet from "./HemsChangePasswordSheet.vue";
import { registerHemsBackOverlay } from "@src/utils/hemsBackButton.js";

const ENERGY_LABELS = {
    ALL: "전체",
    Elec: "전기",
    Gas: "가스",
    Water: "수도",
    Wtr: "수도",
};

function currentYmInput() {
    const d = new Date();
    return `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function prevYmInput() {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function shiftRegYm(ym, delta) {
    const s = String(ym).replace(/\D/g, "").slice(0, 6);
    const y = parseInt(s.slice(0, 4), 10);
    const m = parseInt(s.slice(4, 6), 10) - 1;
    const d = new Date(y, m + delta, 1);
    return `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function monthRangeForTrail(regYm, count = 6) {
    const to = String(regYm).replace(/\D/g, "").slice(0, 6);
    const from = shiftRegYm(to, -(count - 1));
    return { from, to };
}

function buildCalendarMonthOptions(count = 12) {
    const d = new Date();
    const list = [];
    for (let i = 0; i < count; i++) {
        const x = new Date(d.getFullYear(), d.getMonth() - i, 1);
        const regYm = `${x.getFullYear()}${String(x.getMonth() + 1).padStart(2, "0")}`;
        list.push({
            regYm,
            totalAmount: null,
            finalized: false,
        });
    }
    return list;
}

function dayRangeForMonth(regYm) {
    const s = String(regYm).replace(/\D/g, "").slice(0, 6);
    const y = parseInt(s.slice(0, 4), 10);
    const m = parseInt(s.slice(4, 6), 10) - 1;
    const from = new Date(y, m, 1);
    const to = new Date(y, m + 1, 0);
    const pad = (n) => String(n).padStart(2, "0");
    return {
        from: `${y}-${pad(m + 1)}-${pad(from.getDate())}`,
        to: `${y}-${pad(m + 1)}-${pad(to.getDate())}`,
    };
}

export default {
    name: "ResidentEnergyView",
    components: { MobileAppShell, HemsChangePasswordSheet },
    data() {
        return {
            me: null,
            meError: null,
            loadingMe: true,
            loadingUsage: false,
            usagePending: false,
            usageSkeleton: false,
            usageSkeletonTimer: null,
            usageData: null,
            usageEmpty: false,
            /** 사용량 캐시 키: `${energy}:${period}` — 월/일 전환도 즉시 패치 */
            usageByEnergy: {},
            compareByEnergy: {},
            energy: "ALL",
            period: "month",
            regYm: "",
            billingByEnergy: {},
            compare: null,
            amountTrail: null,
            forecast: null,
            /** 청구 요약용 — 선택 에너지와 무관하게 에너지원별 forecast 행 유지 */
            forecastBySource: {},
            enabledEnergySources: [],
            insightPrefetchTimer: null,
            monthSheetOpen: false,
            pwdSheetOpen: false,
            loadingMonthOptions: false,
            monthOptions: [],
            activeTab: "insight",
            shellTabs: [
                { id: "insight", label: "인사이트", icon: "fa-chart-line" },
                { id: "bill", label: "요금", icon: "fa-file-invoice-dollar" },
                { id: "history", label: "이력", icon: "fa-list-alt" },
                { id: "more", label: "더보기", icon: "fa-bars" },
            ],
            historyRows: [],
            historyLoaded: false,
            loadingHistory: false,
            historyError: null,
            historyOpenYms: [],
            displayTotalAmount: 0,
            displayDetailAmount: 0,
            amountAnimRaf: null,
            detailAnimRaf: null,
        };
    },
    computed: {
        /** 사용 분석 스켈레톤 — pending 후 지연 표시 */
        showUsageSkeleton() {
            return this.usageSkeleton;
        },
        energyList() {
            const sources = (this.enabledEnergySources || []).map((key) => ({
                key,
                label: ENERGY_LABELS[key] || key,
            }));
            if (!sources.length) return [];
            return [{ key: "ALL", label: ENERGY_LABELS.ALL }, ...sources];
        },
        energySourcesOnly() {
            return (this.enabledEnergySources || []).map((key) => ({
                key,
                label: ENERGY_LABELS[key] || key,
            }));
        },
        isAllEnergy() {
            return String(this.energy || "").toUpperCase() === "ALL";
        },
        /** 요금·인사이트만 공용 컨텍스트(월·에너지원). 이력·더보기는 숨김 */
        showEnergyContext() {
            return this.activeTab === "bill" || this.activeTab === "insight";
        },
        loginUserId() {
            return this.$store.getters.loginUser?.userId || "";
        },
        displayZoneName() {
            if (!this.me) return "-";
            if (this.me.roomName) return this.me.roomName;
            return this.me.zonePath?.length ? this.me.zonePath.slice(-2).join(" ") : this.me.zoneName;
        },
        zonePathText() {
            return this.me?.zonePath?.join(" › ") || "";
        },
        headerMonthLabel() {
            return this.fmtYmBillLabel(this.regYm);
        },
        /** 선택 청구월의 검침 기간 — forecast / monthOptions / billing 순 */
        selectedBillingPeriod() {
            const ym = String(this.regYm || "")
                .replace(/\D/g, "")
                .slice(0, 6);
            if (!ym) return null;
            const fc = this.forecast;
            if (
                fc &&
                String(fc.regYm || "")
                    .replace(/\D/g, "")
                    .slice(0, 6) === ym &&
                fc.billingStartAt &&
                fc.billingEndAt
            ) {
                return { start: fc.billingStartAt, end: fc.billingEndAt };
            }
            const opt = (this.monthOptions || []).find(
                (o) =>
                    String(o.regYm || "")
                        .replace(/\D/g, "")
                        .slice(0, 6) === ym,
            );
            if (opt?.billingStartAt && opt?.billingEndAt) {
                return { start: opt.billingStartAt, end: opt.billingEndAt };
            }
            const b = this.curBilling;
            if (b?.billingStartAt && b?.billingEndAt) {
                return { start: b.billingStartAt, end: b.billingEndAt };
            }
            return null;
        },
        selectedMonthLabel() {
            return this.fmtYmBillLabel(this.regYm);
        },
        billingSupported() {
            return !!this.me?.sysNodeIdx;
        },
        /** history rows → 월 그룹 + 전년 동월 YoY (프론트 매칭, API 변경 없음) */
        historyMonths() {
            const byYm = {};
            for (const row of this.historyRows || []) {
                const ym = String(row.regYm || "")
                    .replace(/\D/g, "")
                    .slice(0, 6);
                if (!ym) continue;
                if (!byYm[ym]) {
                    byYm[ym] = { regYm: ym, totalAmount: 0, byEnergy: [] };
                }
                byYm[ym].totalAmount += Number(row.totalAmount || 0);
                byYm[ym].byEnergy.push(row);
            }
            const months = Object.values(byYm).sort((a, b) => b.regYm.localeCompare(a.regYm));
            for (const m of months) {
                const prev = byYm[shiftRegYm(m.regYm, -12)];
                if (prev && prev.totalAmount > 0) {
                    const cur = m.totalAmount;
                    const base = prev.totalAmount;
                    m.yoyPct = base === 0 ? null : ((cur - base) / base) * 100;
                } else {
                    m.yoyPct = null;
                }
            }
            return months;
        },
        currentUnit() {
            return this.curBilling?.usageUnit || this.findUsageSource()?.unit || "kWh";
        },
        curBilling() {
            if (this.isAllEnergy) return null;
            return this.billingByEnergy[this.energy] || null;
        },
        forecastByEnergyMap() {
            const map = { ...(this.forecastBySource || {}) };
            for (const row of this.forecast?.byEnergy || []) {
                if (row?.energySource) map[row.energySource] = row;
            }
            return map;
        },
        /** B(실측완료) 또는 C(검침중) — 추정/예상 금액 패널 (선택 에너지 기준) */
        showEstimatePanel() {
            if (this.isAllEnergy) {
                return Object.values(this.forecastByEnergyMap).some((r) => this.isEstimateAmountStatus(r));
            }
            return this.isEstimateAmountStatus(this.curForecast);
        },
        /** @deprecated showEstimatePanel 사용 */
        showForecastPanel() {
            return this.showEstimatePanel;
        },
        forecastAmountTotal() {
            if (this.forecast?.totals?.amountForecast != null && this.isAllEnergy) {
                return Number(this.forecast.totals.amountForecast) || 0;
            }
            return Object.values(this.forecastByEnergyMap)
                .filter((r) => this.isEstimateAmountStatus(r))
                .reduce((s, r) => s + Number(r.amountForecast || r.amountEstimate || 0), 0);
        },
        /** 청구 요약 합계용 — 전 에너지원 캐시 기준 */
        hasSummaryEstimate() {
            return this.energySourcesOnly.some((e) => {
                const b = this.billingByEnergy[e.key];
                if (b?.billingAvailable) return false;
                return this.isEstimateAmountStatus(this.forecastByEnergyMap[e.key]);
            });
        },
        summaryEstimateTotal() {
            return this.energySourcesOnly.reduce((sum, e) => {
                const b = this.billingByEnergy[e.key];
                if (b?.billingAvailable) return sum + Number(b.totalAmount || 0);
                const fc = this.forecastByEnergyMap[e.key];
                if (this.isEstimateAmountStatus(fc)) {
                    return sum + Number(fc.amountForecast || fc.amountEstimate || 0);
                }
                return sum;
            }, 0);
        },
        forecastDisclaimer() {
            const shock =
                this.hasShockCaution ||
                this.curForecast?.shockFlag ||
                this.forecast?.shockFlag;
            const base = shock
                ? this.curForecast?.shockCaution ||
                  this.forecast?.shockCaution ||
                  this.forecast?.disclaimer ||
                  "사용량·요금 변동이 커서 참고용입니다. 확정 청구와 다를 수 있어요."
                : this.forecast?.disclaimer ||
                  (this.forecast?.status === "measured_pending"
                      ? "실 계측 완료 · 청구 전 · 추정"
                      : "확정 전 · 추정");
            const p = this.selectedBillingPeriod;
            const range = p ? this.formatBillingRangeShort(p.start, p.end) : "";
            return range ? `검침 ${range} · ${base}` : base;
        },
        estimatePanelTitle() {
            if (this.hasShockCaution) return "참고 요금";
            if (this.forecast?.status === "measured_pending" || this.isMeasuredPendingBundle) {
                return "청구 전 추정 요금";
            }
            return "검침 중 예상 요금";
        },
        isMeasuredPendingBundle() {
            const rows = Object.values(this.forecastByEnergyMap);
            return (
                this.forecast?.status === "measured_pending" ||
                (rows.some((r) => this.isMeasuredPendingStatus(r)) &&
                    !rows.some((r) => this.isInProgressForecastStatus(r)))
            );
        },
        curForecast() {
            if (this.isAllEnergy) return null;
            return this.forecastByEnergyMap[this.energy] || null;
        },
        curForecastHasEstimate() {
            return this.isEstimateAmountStatus(this.curForecast);
        },
        pendingAmountLabel() {
            return this.isMeasuredPendingStatus(this.curForecast) ? "추정 요금" : "예상 요금";
        },
        pendingUsageLabel() {
            return this.isMeasuredPendingStatus(this.curForecast) ? "실측 사용량" : "사용량";
        },
        pendingBillingHint() {
            if (this.isEstimateAmountStatus(this.curForecast)) return this.forecastDisclaimer;
            if (this.curForecast?.status === "insufficient_data") return "아직 이번 달 사용 데이터가 없어요";
            return this.usageEstimateHint || "확정 후 요금·검침 기간이 표시됩니다.";
        },
        /**
         * HERO 메타 — 단지 검침 기준일 (A안). 예: "검침일 15일"
         * 기간·확정일은 오해·노이즈가 커서 표시하지 않는다.
         */
        billingMetaText() {
            if (!this.curBilling?.billingAvailable) return "";
            const day = Number(this.me?.readingDay);
            if (!Number.isFinite(day) || day < 1 || day > 31) return "";
            return `검침일 ${day}일`;
        },
        /** 가스·수도는 공동설비 배분이 항상 0이라 영수증이 총액 반복에 그친다 */
        showReceipt() {
            const b = this.curBilling;
            if (!b?.billingAvailable) return false;
            return Number(b.allocatedCommonCost || 0) !== 0 || this.hasManualAdjust;
        },
        hasManualAdjust() {
            const amt = Number(this.curBilling?.manualAdjustAmount);
            return this.curBilling?.billingAvailable && !Number.isNaN(amt) && amt !== 0;
        },
        manualAdjustSigned() {
            const amt = Math.trunc(Number(this.curBilling?.manualAdjustAmount || 0));
            return (amt > 0 ? "+" : "") + this.formatWon(amt);
        },
        manualAdjustClass() {
            const amt = Number(this.curBilling?.manualAdjustAmount || 0);
            return amt > 0 ? "is-up" : "is-down";
        },
        curUsageQty() {
            return this.resolveDisplayUsageQty(this.curBilling, this.curForecast);
        },
        totalAmount() {
            // 확정+미확정 혼합월은 행과 동일하게 summaryEstimateTotal (확정분+추정분)
            const hasFinal = Object.values(this.billingByEnergy).some((b) => b?.billingAvailable);
            if (hasFinal || this.hasSummaryEstimate) return this.summaryEstimateTotal;
            if (this.showEstimatePanel) return this.forecastAmountTotal;
            return 0;
        },
        showTotalAmount() {
            return (
                Object.values(this.billingByEnergy).some((b) => b?.billingAvailable) ||
                this.hasSummaryEstimate ||
                this.showEstimatePanel
            );
        },
        /** 합계에 추정·예상분이 하나라도 포함되면 「약」 */
        totalAmountIsEstimate() {
            return this.hasSummaryEstimate || this.hasShockCaution
                ? true
                : this.showEstimatePanel &&
                      !Object.values(this.billingByEnergy).some((b) => b?.billingAvailable);
        },
        totalAmountIsForecast() {
            return this.totalAmountIsEstimate;
        },
        hasShockCaution() {
            if (this.forecast?.shockFlag) return true;
            return Object.values(this.forecastByEnergyMap).some((r) => r?.shockFlag);
        },
        totalAmountSummaryLabel() {
            if (this.hasShockCaution && this.totalAmountIsEstimate) return "참고 합계";
            if (!this.totalAmountIsEstimate) return "합계";
            // 혼합월(확정+추정): 참고 합계
            const hasFinal = Object.values(this.billingByEnergy).some((b) => b?.billingAvailable);
            if (hasFinal && this.hasSummaryEstimate) return "참고 합계";
            return this.isMeasuredPendingBundle ? "추정 합계" : "예상 합계";
        },
        /**
         * 청구 요약표 — billingByEnergy 가 이미 전 에너지 청구를 담고 있어 추가 호출이 없다.
         * 미확정이면 forecast 참고/예상 요금을 붙인다 (선택 에너지와 무관하게 캐시 사용).
         */
        billingSummaryRows() {
            return this.energySourcesOnly.map((e) => {
                const b = this.billingByEnergy[e.key] || null;
                const fc = this.forecastByEnergyMap[e.key] || null;
                const available = !!b?.billingAvailable;
                const hasEstimateAmount = !available && this.isEstimateAmountStatus(fc);
                const displayQty = this.resolveDisplayUsageQty(b, fc);
                const unit = (available ? b?.usageUnit : fc?.usageUnit || b?.usageUnit) || "";
                return {
                    key: e.key,
                    label: e.label,
                    available,
                    forecastOk: hasEstimateAmount,
                    hasEstimateAmount,
                    amount: available ? Number(b.totalAmount || 0) : null,
                    amountForecast: hasEstimateAmount ? Number(fc.amountForecast || fc.amountEstimate || 0) : null,
                    usageText:
                        displayQty !== null && displayQty !== undefined
                            ? `${this.formatUsage(displayQty)}${unit ? ` ${unit}` : ""}`
                            : "",
                    pendingStatus: fc?.status || null,
                };
            });
        },
        allEnergyInsightRows() {
            return this.energySourcesOnly.map((e) => {
                const b = this.billingByEnergy[e.key] || null;
                const fc = this.forecastByEnergyMap[e.key] || null;
                const available = !!b?.billingAvailable;
                const hasEstimateAmount = !available && this.isEstimateAmountStatus(fc);
                const displayQty = this.resolveDisplayUsageQty(b, fc);
                const unit = (available ? b?.usageUnit : fc?.usageUnit || b?.usageUnit) || "";
                return {
                    key: e.key,
                    label: e.label,
                    available,
                    forecastOk: hasEstimateAmount,
                    hasEstimateAmount,
                    usageText:
                        displayQty !== null && displayQty !== undefined
                            ? `${this.formatUsage(displayQty)}${unit ? ` ${unit}` : ""}`
                            : "—",
                    amountText: available
                        ? `${this.formatWon(b.totalAmount)}원`
                        : hasEstimateAmount
                          ? `약 ${this.formatWon(fc.amountForecast || fc.amountEstimate)}원`
                          : fc?.status === "insufficient_data"
                            ? "데이터 부족"
                            : fc?.status === "no_prior_bill"
                              ? "예측 불가"
                              : "확정 전",
                };
            });
        },
        showCompareSection() {
            return true;
        },
        isAmountCompare() {
            return this.isAllEnergy || this.compare?.metric === "amount";
        },
        prevAmountPct() {
            const c = this.compare?.compare?.prevMonth;
            if (!c?.dataAvailable || c.amountDeltaPct === null || c.amountDeltaPct === undefined) {
                return null;
            }
            return Number(c.amountDeltaPct);
        },
        usageEstimateHint() {
            const b = this.curBilling;
            if (!b || b.billingAvailable || !b.usageIsEstimate) return null;
            return "달력 월 합계 기준이며, 확정 시 검침 기간 사용량과 다를 수 있습니다.";
        },
        /**
         * 비교 섹션 결론 — 문장 안의 핵심만 강조하기 위해 조각으로 반환한다.
         * 면적 밴드(±3㎡)는 "유사 면적 평균" 블록의 grp-meta 와 중복이라 넣지 않는다.
         * 전체(ALL)는 요금(원) 기준.
         */
        compareInsight() {
            const sa = this.compare?.compare?.sameArea;
            if (!sa?.dataAvailable) return null;
            const useAmount = this.isAmountCompare;
            const rawPct = useAmount ? sa.myAmountVsAvgPct : sa.myUsageVsAvgPct;
            if (rawPct == null) return null;
            const pct = Number(rawPct) || 0;
            const abs = Math.abs(Math.round(pct));
            const sub = useAmount ? "유사 면적 이웃 요금 평균 대비" : "유사 면적 이웃 평균 대비";
            const tail = useAmount ? "내고 있어요" : "쓰고 있어요";
            if (abs === 0) {
                return { tone: "is-normal", icon: "fa fa-check-circle", key: "비슷하게", tail, sub };
            }
            return pct < 0
                ? { tone: "is-save", icon: "fa fa-leaf", key: `${abs}% 적게`, tail, sub }
                : {
                      tone: "is-over",
                      icon: "fa fa-exclamation-triangle",
                      key: `${abs}% 더`,
                      tail,
                      sub,
                  };
        },
        /**
         * 무엇을 보고 있는지 알려주는 기간 표기.
         * 월별은 청구월 트레일(선택월 기준 과거 N개월)이라 "검침 기간" 대신
         * "최근 N개월 청구 (시작 ~ 끝)"으로 달력 소비월과의 오해를 줄인다.
         */
        usagePeriodText() {
            const rows = this.findUsageSource()?.series || [];
            if (!rows.length) return "";
            const align = this.usageData?.usageAlign;

            if (this.period === "month") {
                const start = this.formatYmDot(rows[0].regDt);
                const end = this.formatYmDot(rows[rows.length - 1].regDt);
                if (!start || !end) return "";
                const n = rows.length;
                const range = `${start} ~ ${end}`;
                // BILLING_PERIOD: 각 점은 청구월의 검침기간 합 — "청구"로 달력월과 구분
                if (align === "BILLING_PERIOD") {
                    return `최근 ${n}개월 청구 (${range})`;
                }
                return `최근 ${n}개월 (${range})`;
            }

            let start = this.formatBillingDate(rows[0].regDt);
            let end = this.formatBillingDate(rows[rows.length - 1].regDt);
            if (align === "BILLING_PERIOD" || align === "BILLING_PERIOD_ESTIMATE") {
                start = this.formatBillingDate(this.usageData.billingStartAt) || start;
                end = this.formatBillingDate(this.usageData.billingEndAt) || end;
            }
            if (!start || !end) return "";
            const suffix = align === "BILLING_PERIOD_ESTIMATE" ? " (예상)" : "";
            return `${start} ~ ${end}${suffix}`;
        },
        /** 사용량 0/무효로 분석에서 뺀 달·날을 문장으로 — 개수만 있으면 "구간"이 무엇인지 알 수 없다 */
        usageDroppedNote() {
            const s = this.usageStats;
            if (!s?.droppedCount) return "";
            const labels = s.droppedLabels || [];
            const unit = this.periodUnitLabel; // 달 | 날

            if (this.period === "day" && labels.length > 4) {
                return `사용량 없는 ${labels.length}일은 분석에서 제외했어요.`;
            }
            if (!labels.length) {
                return `사용량 데이터가 없는 ${s.droppedCount}개 ${unit}은 분석에서 제외했어요.`;
            }
            return `${labels.join(" · ")}은 사용량 데이터가 없어 분석에서 제외했어요.`;
        },
        compareRows() {
            const c = this.compare?.compare;
            const current = this.compare?.current;
            const unit = this.currentUnit;
            const curAmount = current?.billingAvailable ? Number(current.totalAmount || 0) : null;
            const prevMonth = c?.prevMonth;
            const prevYear = c?.prevYearSameMonth;
            const sameArea = c?.sameArea;

            /**
             * 기준값은 백엔드가 내려준 실측치를 그대로 쓴다.
             * 변화율(deltaPct)은 소수 1자리로 반올림되어 있어, 당월값 ÷ (1+pct) 역산은
             * 실제 전월 청구액과 수십 원 어긋난다.
             */
            const buildMetric = (ref, pct, metricUnit) => {
                const n = Number(ref);
                return {
                    ref: Number.isFinite(n) ? n : null,
                    pct: Number(pct) || 0,
                    unit: metricUnit || "",
                };
            };

            const amountOnly = this.isAmountCompare;
            const amountAvailable = (block) =>
                block?.dataAvailable &&
                block?.billingAvailable &&
                current?.billingAvailable &&
                block.amountDeltaPct != null &&
                curAmount !== null;

            const sameAreaMeta = sameArea?.dataAvailable
                ? `±${sameArea.areaBandM2 || 3}㎡ · ${sameArea.peerCount}세대`
                : null;

            return [
                {
                    label: "전월 대비",
                    available: amountOnly ? !!prevMonth?.billingAvailable : !!prevMonth?.dataAvailable,
                    neutral: false,
                    meta: this.fmtYmDot(shiftRegYm(this.regYm, -1)),
                    refColLabel: "전월",
                    usage:
                        !amountOnly && prevMonth?.dataAvailable
                            ? buildMetric(prevMonth.usageQty, prevMonth.usageDeltaPct, unit)
                            : null,
                    amount: amountAvailable(prevMonth)
                        ? buildMetric(prevMonth.totalAmount, prevMonth.amountDeltaPct, "원")
                        : null,
                    amountPending: prevMonth?.dataAvailable && !current?.billingAvailable,
                },
                {
                    label: "전년 동월",
                    available: amountOnly ? !!prevYear?.billingAvailable : !!prevYear?.dataAvailable,
                    neutral: false,
                    meta: this.fmtYmDot(shiftRegYm(this.regYm, -12)),
                    refColLabel: "전년",
                    usage:
                        !amountOnly && prevYear?.dataAvailable
                            ? buildMetric(prevYear.usageQty, prevYear.usageDeltaPct, unit)
                            : null,
                    amount: amountAvailable(prevYear)
                        ? buildMetric(prevYear.totalAmount, prevYear.amountDeltaPct, "원")
                        : null,
                    amountPending: prevYear?.dataAvailable && !current?.billingAvailable,
                },
                {
                    // 세대원 수·재실 시간 차이를 반영할 수 없어 증감에 가치판단 색을 넣지 않는다
                    label: "유사 면적 평균",
                    available: !!sameArea?.dataAvailable,
                    neutral: true,
                    meta: sameAreaMeta,
                    refColLabel: "평균",
                    usage:
                        !amountOnly && sameArea?.dataAvailable
                            ? buildMetric(sameArea.avgUsageQty, sameArea.myUsageVsAvgPct, unit)
                            : null,
                    amount:
                        sameArea?.dataAvailable &&
                        current?.billingAvailable &&
                        sameArea.myAmountVsAvgPct != null &&
                        curAmount !== null
                            ? buildMetric(sameArea.avgTotalAmount, sameArea.myAmountVsAvgPct, "원")
                            : null,
                    amountPending: sameArea?.dataAvailable && !current?.billingAvailable,
                },
            ];
        },
        compareShownRows() {
            return this.compareRows.filter((r) => r.available);
        },
        /** 데이터 없는 비교 대상은 블록 대신 하단 한 줄로 모은다 */
        compareMissingText() {
            const missing = this.compareRows.filter((r) => !r.available).map((r) => r.label);
            if (!missing.length) return "";
            return `${missing.join(" · ")} 비교는 아직 데이터가 없어요`;
        },
        /** 미확정 안내를 블록마다 반복하지 않고 섹션에 한 번만 */
        compareAmountPendingText() {
            const pending = this.compareRows.some((r) => r.available && r.amountPending);
            return pending ? "요금은 청구 확정 후 비교돼요" : "";
        },
        periodUnitLabel() {
            return this.period === "month" ? "달" : "날";
        },
        /** 선택 구간이 아직 진행 중인지 — 진행 중이면 절약/과다 판정을 보류한다 */
        periodPartial() {
            if (this.usageData?.usageAlign === "BILLING_PERIOD_ESTIMATE") return true;
            if (this.curBilling && !this.curBilling.billingAvailable) return true;
            const now = new Date();
            const nowYm = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, "0")}`;
            return this.regYm >= nowYm;
        },
        /**
         * 분석 표본 정리
         * - 월별 청구 트레일은 데이터 없는 달을 value:0 으로 채워 보내므로 0은 표본에서 제외
         * - 일별 + 진행 중 기간이면 마지막 날은 아직 누적 중이라 제외
         */
        usageStats() {
            if (this.usageEmpty || !this.usageData) return null;
            const series = this.getChartSeries();
            const raw = series.values || [];

            const all = raw.map((v, i) => ({
                v: Number(v),
                label: this.insightPointLabel(series, i),
            }));
            const points = all.filter((p) => Number.isFinite(p.v) && p.v > 0);
            if (!points.length) return null;

            const droppedLabels = all
                .filter((p) => !(Number.isFinite(p.v) && p.v > 0))
                .map((p) => p.label)
                .filter(Boolean);

            const dropTail = this.period === "day" && this.periodPartial && points.length > 1;
            const sample = dropTail ? points.slice(0, -1) : points;
            const current = sample[sample.length - 1];

            const values = sample.map((p) => p.v);
            const avg = values.reduce((a, b) => a + b, 0) / values.length;

            return {
                count: sample.length,
                droppedCount: droppedLabels.length || raw.length - points.length,
                droppedLabels,
                avg,
                max: sample.reduce((a, b) => (b.v > a.v ? b : a)),
                min: sample.reduce((a, b) => (b.v < a.v ? b : a)),
                current,
                ratio: avg > 0 ? current.v / avg : 1,
            };
        },
        /** 표본을 사람이 읽는 문장으로 해석 — 기준은 "내 과거 평균"(이웃 비교는 아래 비교 섹션 담당) */
        insightSummary() {
            const s = this.usageStats;
            if (!s) return null;

            const base = {
                ...s,
                comparable: false,
                gaugePercent: 50,
                tone: "is-mute",
            };

            if (s.count < 3) {
                return {
                    ...base,
                    icon: "fa fa-info-circle",
                    title: "비교할 기간이 아직 부족해요",
                    desc: "데이터가 조금 더 쌓이면 사용 패턴을 분석해 드려요.",
                };
            }
            if (this.period === "month" && this.periodPartial) {
                return {
                    ...base,
                    icon: "fa fa-hourglass-half",
                    title: "이번 기간은 아직 집계 중이에요",
                    desc: "청구가 확정되면 정확한 분석을 보여드려요.",
                };
            }

            const ratio = s.ratio;
            let tone = "is-normal";
            let icon = "fa fa-check-circle";
            let title = "평소와 비슷하게 쓰고 있어요";
            let desc = `내 평균 ${this.formatUsage(s.avg)} ${this.currentUnit} 수준이에요.`;

            if (ratio >= 1.2) {
                tone = "is-over";
                icon = "fa fa-exclamation-triangle";
                title = "내 평균보다 많이 쓰고 있어요";
                desc = "냉난방이나 상시 가전 사용이 늘었는지 살펴보세요.";
            } else if (ratio <= 0.8) {
                tone = "is-save";
                icon = "fa fa-leaf";
                title = "내 평균보다 적게 쓰고 있어요";
                desc = "사용량이 줄면 배분되는 요금도 낮아질 수 있어요.";
            }

            return {
                ...s,
                comparable: true,
                tone,
                icon,
                title,
                desc,
                // 내 평균을 50% 에 고정 → ratio 0.8 = 40%, 1.2 = 60% 로 게이지 구간 경계와 일치
                gaugePercent: Math.max(3, Math.min(ratio * 50, 100)),
                ratioText: `내 평균의 ${Math.round(ratio * 100)}%`,
            };
        },
        /** 전체 탭 — 최근 N개월 FINAL 요금 합 표본 */
        amountStats() {
            const series = this.amountTrail?.series;
            if (!Array.isArray(series) || !series.length) return null;

            const all = series.map((row) => ({
                v: Number(row.value),
                label: row.label || this.fmtYmLabel(row.regYm || row.regDt),
                billingAvailable: !!row.billingAvailable,
            }));
            const points = all.filter((p) => p.billingAvailable && Number.isFinite(p.v) && p.v > 0);
            if (!points.length) return null;

            const droppedLabels = all
                .filter((p) => !(p.billingAvailable && Number.isFinite(p.v) && p.v > 0))
                .map((p) => p.label)
                .filter(Boolean);

            const sample = points;
            const current = sample[sample.length - 1];
            const values = sample.map((p) => p.v);
            const avg = values.reduce((a, b) => a + b, 0) / values.length;

            return {
                count: sample.length,
                droppedCount: droppedLabels.length,
                droppedLabels,
                avg,
                max: sample.reduce((a, b) => (b.v > a.v ? b : a)),
                min: sample.reduce((a, b) => (b.v < a.v ? b : a)),
                current,
                ratio: avg > 0 ? current.v / avg : 1,
                partial: !this.compare?.current?.billingAvailable,
            };
        },
        amountPeriodText() {
            const rows = this.amountTrail?.series || [];
            if (!rows.length) return "";
            const start = this.formatYmDot(rows[0].regDt || rows[0].regYm);
            const end = this.formatYmDot(rows[rows.length - 1].regDt || rows[rows.length - 1].regYm);
            if (!start || !end) return "";
            return `최근 ${rows.length}개월 청구 (${start} ~ ${end})`;
        },
        amountDroppedNote() {
            const s = this.amountStats;
            if (!s?.droppedCount) return "";
            const labels = s.droppedLabels || [];
            if (!labels.length) {
                return `확정 요금이 없는 ${s.droppedCount}개월은 분석에서 제외했어요.`;
            }
            return `${labels.join(" · ")}은 확정 요금이 없어 분석에서 제외했어요.`;
        },
        amountInsightSummary() {
            const s = this.amountStats;
            if (!s) return null;

            const base = {
                ...s,
                comparable: false,
                gaugePercent: 50,
                tone: "is-mute",
            };

            if (s.count < 3) {
                return {
                    ...base,
                    icon: "fa fa-info-circle",
                    title: "비교할 기간이 아직 부족해요",
                    desc: "확정 청구가 조금 더 쌓이면 요금 패턴을 분석해 드려요.",
                };
            }
            if (s.partial) {
                return {
                    ...base,
                    icon: "fa fa-hourglass-half",
                    title: "이번 달은 아직 집계 중이에요",
                    desc: "전 에너지원 청구가 확정되면 정확한 분석을 보여드려요.",
                };
            }

            const ratio = s.ratio;
            let tone = "is-normal";
            let icon = "fa fa-check-circle";
            let title = "평소와 비슷하게 내고 있어요";
            let desc = `내 평균 ${this.formatWon(s.avg)}원 수준이에요.`;

            if (ratio >= 1.2) {
                tone = "is-over";
                icon = "fa fa-exclamation-triangle";
                title = "내 평균보다 많이 내고 있어요";
                desc = "에너지원별 사용·요금 변화를 함께 살펴보세요.";
            } else if (ratio <= 0.8) {
                tone = "is-save";
                icon = "fa fa-leaf";
                title = "내 평균보다 적게 내고 있어요";
                desc = "전체 요금이 평소보다 낮은 달이에요.";
            }

            return {
                ...s,
                comparable: true,
                tone,
                icon,
                title,
                desc,
                gaugePercent: Math.max(3, Math.min(ratio * 50, 100)),
                ratioText: `내 평균의 ${Math.round(ratio * 100)}%`,
            };
        },
    },
    watch: {
        period() {
            if (this.isAllEnergy) return;
            this.ensureInsightEnergy(this.energy).then(() => this.scheduleInsightPrefetch());
        },
        totalAmount(n) {
            this.animateAmount("displayTotalAmount", "amountAnimRaf", n);
        },
        "curBilling.totalAmount"(n) {
            const target = this.curBilling?.billingAvailable && n != null && Number.isFinite(Number(n)) ? Number(n) : 0;
            if (!this.curBilling?.billingAvailable) {
                this.displayDetailAmount = 0;
                return;
            }
            this.animateAmount("displayDetailAmount", "detailAnimRaf", target);
        },
        activeTab(tab) {
            if (tab === "history") this.loadHistory();
            window.scrollTo({ top: 0, behavior: "auto" });
        },
    },
    beforeDestroy() {
        if (this._unregisterHemsBack) {
            this._unregisterHemsBack();
            this._unregisterHemsBack = null;
        }
        if (this.amountAnimRaf) cancelAnimationFrame(this.amountAnimRaf);
        if (this.detailAnimRaf) cancelAnimationFrame(this.detailAnimRaf);
        if (this.insightPrefetchTimer) clearTimeout(this.insightPrefetchTimer);
        if (this.usageSkeletonTimer) clearTimeout(this.usageSkeletonTimer);
    },
    async created() {
        this.pageOptions.pageEmpty = true;
        this._unregisterHemsBack = registerHemsBackOverlay(() => {
            if (this.pwdSheetOpen) {
                this.pwdSheetOpen = false;
                return true;
            }
            if (!this.monthSheetOpen) return false;
            this.closeMonthSheet();
            return true;
        });
        // 미인증 리다이렉트는 App.vue / requireAuth 가 담당 — 여기서 replace 하면 이중 네비로 NavigationDuplicated
        if (!this.$store.getters.isAuthenticated) return;
        await this.initPage();
    },
    /**
     * pageEmpty 는 App.vue 레이아웃(사이드바 등) 표시 여부.
     * destroy 시 무조건 false 로 되돌리면 로그인(created 에서 true) 직후
     * 이 컴포넌트 destroy 가 덮어쓸 수 있어, public 목적지면 유지한다.
     */
    beforeRouteLeave(to, from, next) {
        if (!(to.meta && to.meta.public)) {
            this.pageOptions.pageEmpty = false;
        }
        next();
    },
    methods: {
        showErrorToast(message) {
            const msg = message || "데이터를 불러오지 못했습니다.";
            if (this.$toast?.error) {
                this.$toast.error(msg);
            } else if (this.$bvToast) {
                this.$bvToast.toast(msg, {
                    variant: "danger",
                    solid: true,
                    autoHideDelay: 4000,
                });
            }
        },
        apiErrorMessage(err, fallback) {
            return err?.response?.data?.detail || err?.message || fallback;
        },
        formatBillingDate(value) {
            const s = String(value || "").replace(/\D/g, "");
            if (s.length < 8) return "";
            return `${s.slice(0, 4)}.${s.slice(4, 6)}.${s.slice(6, 8)}`;
        },
        /** YYYYMMDD → "7.1" (연도 생략 짧은 표기) */
        formatBillingDayShort(value) {
            const s = String(value || "").replace(/\D/g, "");
            if (s.length < 8) return "";
            return `${parseInt(s.slice(4, 6), 10)}.${parseInt(s.slice(6, 8), 10)}`;
        },
        /** 검침 기간 "7.1–7.30" / 연도 다르면 "2025.12.15–1.14" */
        formatBillingRangeShort(startAt, endAt) {
            const a = String(startAt || "").replace(/\D/g, "");
            const b = String(endAt || "").replace(/\D/g, "");
            if (a.length < 8 || b.length < 8) return "";
            const sameYear = a.slice(0, 4) === b.slice(0, 4);
            if (sameYear) {
                return `${this.formatBillingDayShort(a)}–${this.formatBillingDayShort(b)}`;
            }
            return `${a.slice(0, 4)}.${this.formatBillingDayShort(a)}–${this.formatBillingDayShort(b)}`;
        },
        /** yyyyMM → "2026.01" */
        formatYmDot(value) {
            const s = String(value || "").replace(/\D/g, "");
            return s.length >= 6 ? `${s.slice(0, 4)}.${s.slice(4, 6)}` : "";
        },
        fmtYmLabel(regYm) {
            const s = String(regYm || "").replace(/\D/g, "");
            if (s.length < 6) return regYm || "-";
            return `${s.slice(0, 4)}년 ${parseInt(s.slice(4, 6), 10)}월`;
        },
        /** 청구월 라벨 — 달력월과 혼동을 줄이기 위해 "청구"를 붙인다 */
        fmtYmBillLabel(regYm) {
            const base = this.fmtYmLabel(regYm);
            return base === "-" ? base : `${base} 청구`;
        },
        monthOptionPeriodText(opt) {
            if (!opt?.billingStartAt || !opt?.billingEndAt) return "";
            const range = this.formatBillingRangeShort(opt.billingStartAt, opt.billingEndAt);
            return range ? `검침 ${range}` : "";
        },
        fmtYmDot(regYm) {
            const s = String(regYm || "").replace(/\D/g, "");
            if (s.length < 6) return "";
            return `${s.slice(0, 4)}.${s.slice(4, 6)}`;
        },
        formatWon(v) {
            if (v === null || v === undefined) return "-";
            return Math.trunc(Number(v)).toLocaleString("ko-KR");
        },
        formatUsage(v) {
            if (v === null || v === undefined) return "-";
            return Number(v).toLocaleString("ko-KR", { maximumFractionDigits: 1 });
        },
        pctText(v) {
            const n = Number(v) || 0;
            return (n > 0 ? "+" : "") + n.toFixed(1) + "%";
        },
        deltaTone(pct) {
            const n = Number(pct) || 0;
            // 표기와 색을 일치시키기 위해 반올림 후 0이면 중립
            if (Math.round(Math.abs(n)) === 0) return "is-neutral";
            return n > 0 ? "is-up" : "is-down";
        },
        deltaPctSymbol(pct) {
            const n = Number(pct) || 0;
            const abs = Math.round(Math.abs(n));
            if (abs === 0) return "0%";
            return n > 0 ? `▲ ${abs}%` : `▼ ${abs}%`;
        },
        findUsageSource() {
            const list = this.usageData?.byEnergySource || [];
            if (!list.length) return null;
            const matched = list.find((s) => s.energySourceCode === this.energy);
            if (matched) return matched;
            // chart API는 요청한 에너지원 1건만 반환 — energySource 필드로 일치 확인
            if (this.usageData?.energySource === this.energy) {
                return list[0];
            }
            // 캐시 miss 로딩 중: 선택 에너지와 usageData 가 잠깐 어긋나도 이전 시리즈를 유지해 empty 깜빡임을 막는다
            return list[0];
        },
        setEnergy(key) {
            if (key === this.energy) return;
            this.energy = key;
            this.onEnergyChanged();
        },
        energyLabel(key) {
            return ENERGY_LABELS[key] || key;
        },
        isCurrentCalendarMonth(regYm) {
            return (
                String(regYm || "")
                    .replace(/\D/g, "")
                    .slice(0, 6) === currentYmInput()
            );
        },
        isMeasuredPendingStatus(fc) {
            return fc?.status === "measured_pending";
        },
        isInProgressForecastStatus(fc) {
            return fc?.status === "ok";
        },
        isEstimateAmountStatus(fc) {
            return this.isMeasuredPendingStatus(fc) || this.isInProgressForecastStatus(fc);
        },
        /**
         * 미확정 월 사용량 표시 — billing.usageQty(달력월 0)보다 forecast.usageMtd(검침기간 실측) 우선.
         * 확정이면 billing 사용량.
         */
        resolveDisplayUsageQty(billing, forecast) {
            if (billing?.billingAvailable) {
                const q = Number(billing.usageQty);
                return Number.isFinite(q) ? q : null;
            }
            if (forecast && forecast.usageMtd != null && forecast.usageMtd !== "") {
                const mtd = Number(forecast.usageMtd);
                if (Number.isFinite(mtd)) return mtd;
            }
            if (billing?.usageQty !== undefined && billing?.usageQty !== null) {
                const q = Number(billing.usageQty);
                if (Number.isFinite(q) && q > 0) return q;
            }
            if (billing?.usageQty !== undefined && billing?.usageQty !== null) {
                const q = Number(billing.usageQty);
                return Number.isFinite(q) ? q : null;
            }
            const fromSeries = this.findUsageSource()?.total;
            if (fromSeries != null && Number.isFinite(Number(fromSeries))) return Number(fromSeries);
            return null;
        },
        monthOptionBadge(opt) {
            if (opt?.finalized) return "";
            const status = opt?.meteringStatus;
            if (status === "measured_pending") return "실측 완료 · 청구 전";
            if (status === "in_progress") return "검침 중";
            if (status === "upcoming") return "검침 예정";
            return this.isCurrentCalendarMonth(opt?.regYm) ? "검침 중" : "청구 확정 전";
        },
        shiftRegYmLocal(regYm, deltaMonths) {
            const s = String(regYm || "")
                .replace(/\D/g, "")
                .slice(0, 6);
            if (s.length < 6) return "";
            const d = new Date(Number(s.slice(0, 4)), Number(s.slice(4, 6)) - 1 + deltaMonths, 1);
            return `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, "0")}`;
        },
        setActiveTab(id) {
            if (!id || id === this.activeTab) return;
            this.activeTab = id;
        },
        clearInsightCaches() {
            this.usageByEnergy = {};
            this.compareByEnergy = {};
            this.amountTrail = null;
            this.forecastBySource = {};
        },
        rememberForecast(data) {
            if (!data) return;
            for (const row of data.byEnergy || []) {
                if (row?.energySource) {
                    this.$set(this.forecastBySource, row.energySource, row);
                }
            }
        },
        /** 청구 요약은 전 에너지원 — 캐시에 빠진 소스가 있으면 ALL forecast 로 채움 */
        async ensureSummaryForecast() {
            const sources = this.enabledEnergySources || [];
            if (!sources.length) return;
            const missing = sources.some((s) => !this.forecastBySource[s]);
            if (!missing) return;
            try {
                const params = { energySource: "ALL" };
                if (this.regYm) params.regYm = this.regYm;
                const { data } = await backEndApi.mobileResident.getForecast(params);
                this.rememberForecast(data);
                if (this.isAllEnergy) this.forecast = data;
            } catch (e) {
                // 요약 보강 실패는 토스트 생략 — 선택 에너지 추정은 유지
                console.debug("ensureSummaryForecast failed", e);
            }
        },
        usageCacheKey(energy, period = this.period) {
            return `${energy || this.energy}:${period || this.period}`;
        },
        hasUsageCache(energy, period = this.period) {
            return Object.prototype.hasOwnProperty.call(this.usageByEnergy, this.usageCacheKey(energy, period));
        },
        hasCompareCache(energy) {
            return Object.prototype.hasOwnProperty.call(this.compareByEnergy, energy);
        },
        hasInsightCache(energy) {
            if (String(energy || "").toUpperCase() === "ALL") {
                const cached = this.compareByEnergy.ALL;
                return !!cached && !!(this.amountTrail || cached.amountTrail);
            }
            return this.hasUsageCache(energy) && this.hasCompareCache(energy);
        },
        syncInsightDisplay(energy) {
            if (String(energy || "").toUpperCase() === "ALL") {
                this.usageData = null;
                this.usageEmpty = false;
                if (this.hasCompareCache("ALL")) {
                    const cached = this.compareByEnergy.ALL;
                    this.compare = cached;
                    if (cached?.amountTrail) this.amountTrail = cached.amountTrail;
                } else {
                    this.compare = null;
                }
                return;
            }
            this.amountTrail = null;
            if (this.hasUsageCache(energy)) {
                const data = this.usageByEnergy[this.usageCacheKey(energy)];
                this.usageData = data;
                const list = data?.byEnergySource || [];
                const src =
                    list.find((s) => s.energySourceCode === energy) ||
                    (data?.energySource === energy ? list[0] : null) ||
                    list[0] ||
                    null;
                const hasSeries =
                    this.period === "month" ? src?.series?.length || data?.stackedSeries?.length : src?.series?.length;
                this.usageEmpty = !hasSeries;
            }
            if (this.hasCompareCache(energy)) {
                this.compare = this.compareByEnergy[energy];
            }
        },
        putUsageCache(energy, data, period = this.period) {
            this.$set(this.usageByEnergy, this.usageCacheKey(energy, period), data);
        },
        putCompareCache(energy, data) {
            this.$set(this.compareByEnergy, energy, data);
        },
        clearUsageSkeletonTimer() {
            if (this.usageSkeletonTimer) {
                clearTimeout(this.usageSkeletonTimer);
                this.usageSkeletonTimer = null;
            }
        },
        /** 사용 분석 로딩 — 150ms 이내 응답이면 스켈레톤 생략 */
        beginUsagePending({ immediate = false } = {}) {
            this.usagePending = true;
            this.clearUsageSkeletonTimer();
            if (immediate) {
                this.usageSkeleton = true;
                return;
            }
            this.usageSkeletonTimer = setTimeout(() => {
                if (this.usagePending) this.usageSkeleton = true;
            }, 150);
        },
        endUsagePending() {
            this.usagePending = false;
            this.usageSkeleton = false;
            this.clearUsageSkeletonTimer();
            this.loadingUsage = false;
        },
        buildUsageParamsFor(energy, period = this.period) {
            const energySource = energy || this.energy;
            const chartPeriod = period || this.period;
            if (this.billingSupported) {
                return {
                    chartPeriod,
                    regYm: this.regYm,
                    energySource,
                    count: 6,
                };
            }
            if (chartPeriod === "month") {
                const range = monthRangeForTrail(this.regYm, 6);
                return {
                    timeDsvn: "MONTH",
                    fromDate: range.from,
                    toDate: range.to,
                };
            }
            const range = dayRangeForMonth(this.regYm);
            return {
                timeDsvn: "DAY",
                fromDate: `${range.from} 00:00:00`,
                toDate: `${range.to} 23:59:59`,
            };
        },
        /**
         * 에너지원 인사이트 보장 — 사용량은 energy:period 키.
         * background: prefetch (선택 탭이 아니어도 맵만 채움)
         */
        async ensureInsightEnergy(energy, { background = false } = {}) {
            if (String(energy || "").toUpperCase() === "ALL") {
                const needCompare = !this.hasCompareCache("ALL");
                const needForecast = !this.forecast || String(this.forecast.regYm) !== String(this.regYm);
                if (!needCompare && !needForecast) {
                    if (!background && energy === this.energy) {
                        this.syncInsightDisplay("ALL");
                        this.endUsagePending();
                    }
                    return;
                }
                const showPending = !background && energy === this.energy && needCompare;
                if (showPending) this.beginUsagePending({ immediate: true });
                try {
                    const tasks = [];
                    if (needCompare) {
                        tasks.push(
                            backEndApi.mobileResident
                                .getCompare({ regYm: this.regYm, energySource: "ALL" })
                                .then(({ data }) => {
                                    this.putCompareCache("ALL", data);
                                    if (data?.amountTrail) this.amountTrail = data.amountTrail;
                                }),
                        );
                    }
                    if (needForecast) {
                        tasks.push(
                            backEndApi.mobileResident
                                .getForecast({ regYm: this.regYm, energySource: "ALL" })
                                .then(({ data }) => {
                                    this.rememberForecast(data);
                                    if (this.isAllEnergy) this.forecast = data;
                                }),
                        );
                    }
                    await Promise.all(tasks);
                    if (!background && energy === this.energy) {
                        this.syncInsightDisplay("ALL");
                    }
                } catch (e) {
                    if (!background) {
                        this.showErrorToast(this.apiErrorMessage(e, "에너지 정보를 불러오지 못했습니다."));
                    }
                } finally {
                    if (showPending && energy === this.energy) {
                        this.endUsagePending();
                    }
                }
                return;
            }
            const fetchPeriod = this.period;
            const needUsage = !this.hasUsageCache(energy, fetchPeriod);
            const needCompare = !this.hasCompareCache(energy);
            if (!needUsage && !needCompare) {
                if (!background && energy === this.energy) {
                    this.syncInsightDisplay(energy);
                    this.endUsagePending();
                }
                return;
            }
            // 사용량 캐시 hit 이면 비교 fetch 전에 먼저 패치 (스테일 에너지원 표시 방지)
            if (!needUsage && !background && energy === this.energy) {
                this.syncInsightDisplay(energy);
                this.endUsagePending();
            }

            const showPending = !background && energy === this.energy && needUsage;
            if (showPending) this.beginUsagePending();

            try {
                const tasks = [];
                if (needUsage) {
                    tasks.push(
                        backEndApi.mobileResident
                            .getUsage(this.buildUsageParamsFor(energy, fetchPeriod))
                            .then(({ data }) => {
                                this.putUsageCache(energy, data, fetchPeriod);
                            }),
                    );
                }
                if (needCompare) {
                    tasks.push(
                        backEndApi.mobileResident
                            .getCompare({ regYm: this.regYm, energySource: energy })
                            .then(({ data }) => {
                                this.putCompareCache(energy, data);
                            }),
                    );
                }
                tasks.push(
                    backEndApi.mobileResident
                        .getForecast({ regYm: this.regYm, energySource: energy })
                        .then(({ data }) => {
                            this.rememberForecast(data);
                            if (energy === this.energy) this.forecast = data;
                        }),
                );
                await Promise.all(tasks);
                if (energy === this.energy && fetchPeriod === this.period) {
                    this.syncInsightDisplay(energy);
                }
                await this.ensureSummaryForecast();
            } catch (e) {
                if (!background) {
                    if (
                        needUsage &&
                        energy === this.energy &&
                        fetchPeriod === this.period &&
                        !this.hasUsageCache(energy, fetchPeriod)
                    ) {
                        this.usageEmpty = true;
                        this.usageData = null;
                    }
                    this.showErrorToast(this.apiErrorMessage(e, "에너지 정보를 불러오지 못했습니다."));
                }
            } finally {
                if (showPending && energy === this.energy && fetchPeriod === this.period) {
                    this.endUsagePending();
                }
            }
        },
        async loadForecast() {
            try {
                const params = { energySource: this.energy };
                if (this.regYm) params.regYm = this.regYm;
                const { data } = await backEndApi.mobileResident.getForecast(params);
                this.forecast = data;
                this.rememberForecast(data);
                if (data?.regYm && !this.regYm) {
                    this.regYm = String(data.regYm).replace(/\D/g, "").slice(0, 6);
                }
                await this.ensureSummaryForecast();
            } catch (e) {
                this.forecast = null;
                this.showErrorToast(this.apiErrorMessage(e, "예상 요금을 불러오지 못했습니다."));
            }
        },
        scheduleInsightPrefetch() {
            if (this.isAllEnergy) return;
            if (this.insightPrefetchTimer) clearTimeout(this.insightPrefetchTimer);
            this.insightPrefetchTimer = setTimeout(() => {
                this.prefetchInsightOthers();
            }, 450);
        },
        async prefetchInsightOthers() {
            if (this.isAllEnergy) return;
            const list = (this.enabledEnergySources || []).filter((e) => e && e !== this.energy);
            if (!list.length) return;
            await Promise.all(list.map((e) => this.ensureInsightEnergy(e, { background: true })));
        },
        prefersReducedMotion() {
            return typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
        },
        animateAmount(displayKey, rafKey, target) {
            const to = Math.trunc(Number(target) || 0);
            if (this.prefersReducedMotion()) {
                this[displayKey] = to;
                return;
            }
            if (this[rafKey]) cancelAnimationFrame(this[rafKey]);
            const from = Math.trunc(Number(this[displayKey]) || 0);
            if (from === to) {
                this[displayKey] = to;
                return;
            }
            const duration = 420;
            const start = performance.now();
            const tick = (now) => {
                const t = Math.min(1, (now - start) / duration);
                const eased = 1 - Math.pow(1 - t, 3);
                this[displayKey] = Math.round(from + (to - from) * eased);
                if (t < 1) {
                    this[rafKey] = requestAnimationFrame(tick);
                } else {
                    this[displayKey] = to;
                    this[rafKey] = null;
                }
            };
            this[rafKey] = requestAnimationFrame(tick);
        },
        async onEnergyChanged() {
            if (this.isAllEnergy) {
                if (this.hasInsightCache("ALL")) {
                    this.syncInsightDisplay("ALL");
                    await this.loadForecast();
                    this.endUsagePending();
                    return;
                }
                await this.ensureInsightEnergy("ALL");
                return;
            }
            if (this.hasInsightCache(this.energy)) {
                this.syncInsightDisplay(this.energy);
                await this.loadForecast();
                this.endUsagePending();
                return;
            }
            await this.ensureInsightEnergy(this.energy);
        },
        ensureCurrentMonthOption() {
            const cur = currentYmInput();
            const list = Array.isArray(this.monthOptions) ? [...this.monthOptions] : [];
            const have = new Set(list.map((m) => String(m.regYm)));
            // 관심 청구월(다음·당월·전월)이 목록에 없으면 추가 — BE enrich 와 맞춤
            for (const offset of [2, 1, 0, -1]) {
                const ym = this.shiftRegYmLocal(cur, offset);
                if (ym && !have.has(ym)) {
                    list.push({ regYm: ym, totalAmount: null, finalized: false });
                    have.add(ym);
                }
            }
            this.monthOptions = list.sort((a, b) => String(b.regYm).localeCompare(String(a.regYm)));
        },
        setPeriod(p) {
            this.period = p;
        },
        openMonthSheet() {
            this.monthSheetOpen = true;
            if (!this.monthOptions.length) {
                this.loadMonthOptions();
            }
        },
        closeMonthSheet() {
            this.monthSheetOpen = false;
        },
        async selectMonth(regYm) {
            const ym = String(regYm).replace(/\D/g, "").slice(0, 6);
            if (!ym || ym === this.regYm) {
                this.closeMonthSheet();
                return;
            }
            this.regYm = ym;
            this.closeMonthSheet();
            await this.loadDashboard();
            if (this.activeTab === "bill" || this.activeTab === "insight") {
                window.scrollTo({ top: 0, behavior: "smooth" });
            }
        },
        toggleHistoryMonth(regYm) {
            const i = this.historyOpenYms.indexOf(regYm);
            if (i >= 0) this.historyOpenYms.splice(i, 1);
            else this.historyOpenYms.push(regYm);
        },
        isHistoryOpen(regYm) {
            return this.historyOpenYms.indexOf(regYm) >= 0;
        },
        async loadHistory(force = false) {
            if (!this.billingSupported) {
                this.historyRows = [];
                this.historyLoaded = true;
                this.historyError = null;
                return;
            }
            if (this.historyLoaded && !force) return;
            this.loadingHistory = true;
            this.historyError = null;
            try {
                const to = this.regYm || prevYmInput().replace(/\D/g, "").slice(0, 6);
                const from = shiftRegYm(to, -11);
                const { data } = await backEndApi.mobileResident.getBillingHistory({
                    energySource: "ALL",
                    regYmFrom: from,
                    regYmTo: to,
                });
                this.historyRows = data.rows || [];
                if (data.monthOptions?.length) {
                    this.monthOptions = data.monthOptions;
                }
                this.historyLoaded = true;
            } catch (e) {
                this.historyRows = [];
                this.historyError = this.apiErrorMessage(e, "청구 이력을 불러오지 못했습니다.");
            } finally {
                this.loadingHistory = false;
            }
        },
        buildUsageParams() {
            return this.buildUsageParamsFor(this.energy);
        },
        applyUsageData(data, energy = this.energy) {
            this.putUsageCache(energy, data);
            if (energy === this.energy) {
                this.syncInsightDisplay(energy);
            }
        },
        async loadMonthOptions() {
            this.loadingMonthOptions = true;
            try {
                if (!this.billingSupported) {
                    this.monthOptions = buildCalendarMonthOptions(12);
                    this.ensureCurrentMonthOption();
                    return;
                }
                const { data } = await backEndApi.mobileResident.getBillingHistory({
                    energySource: "ALL",
                });
                this.monthOptions = data.monthOptions?.length ? data.monthOptions : buildCalendarMonthOptions(12);
                this.ensureCurrentMonthOption();
            } catch (e) {
                this.monthOptions = buildCalendarMonthOptions(12);
                this.ensureCurrentMonthOption();
                this.showErrorToast(this.apiErrorMessage(e, "청구 월 목록을 불러오지 못했습니다."));
            } finally {
                this.loadingMonthOptions = false;
            }
        },
        async initPage() {
            this.loadingMe = true;
            this.meError = null;
            try {
                const { data } = await backEndApi.mobileResident.getMe();
                this.me = data;
                const sources = Array.isArray(data.energySources) ? data.energySources.filter(Boolean) : [];
                this.enabledEnergySources = sources;
                if (!sources.length) {
                    this.energy = "Elec";
                } else if (this.energy !== "ALL" && !sources.includes(this.energy)) {
                    this.energy = sources[0];
                } else {
                    this.energy = "ALL";
                }
                // 기본 청구월은 BE resolveDefaultRegYm (실측완료 B 우선) — 달력 당월 강제 금지
                this.regYm = "";
                if (this.billingSupported) {
                    await this.loadMonthOptions();
                } else {
                    this.monthOptions = buildCalendarMonthOptions(12);
                    this.ensureCurrentMonthOption();
                }
                await this.loadDashboard();
                if (data && !this.regYm) {
                    this.regYm = currentYmInput();
                }
            } catch (err) {
                this.meError = err?.response?.data?.detail || err?.message || "세대 정보를 불러오지 못했습니다.";
            } finally {
                this.loadingMe = false;
            }
        },
        async loadDashboard() {
            if (!this.me) return;
            this.loadingUsage = true;
            this.usageEmpty = false;
            this.clearInsightCaches();
            this.beginUsagePending({ immediate: true });
            try {
                const params = {
                    energySource: this.energy,
                };
                if (this.regYm) params.regYm = this.regYm;
                if (!this.isAllEnergy) {
                    // regYm 이 비어 있으면 BE 기본월 확정 후 아래에서 usage 를 재요청한다
                    if (this.regYm) Object.assign(params, this.buildUsageParams());
                }
                const { data } = await backEndApi.mobileResident.getDashboard(params);
                if (data.regYm) this.regYm = String(data.regYm).replace(/\D/g, "").slice(0, 6);
                this.billingByEnergy = data.billing || {};
                this.forecast = data.forecast || null;
                this.rememberForecast(data.forecast);
                if (data.compare) {
                    this.putCompareCache(this.energy, data.compare);
                } else if (this.isAllEnergy) {
                    this.compare = null;
                }
                if (this.isAllEnergy) {
                    this.amountTrail = data.amountTrail || data.compare?.amountTrail || null;
                } else {
                    this.amountTrail = null;
                }
                if (data.monthOptions?.length) {
                    this.monthOptions = data.monthOptions;
                }
                this.ensureCurrentMonthOption();
                if (this.isAllEnergy) {
                    this.usageData = null;
                    this.usageEmpty = false;
                    this.syncInsightDisplay("ALL");
                } else if (data.usage) {
                    this.applyUsageData(data.usage, this.energy);
                } else if (!params.timeDsvn && this.regYm) {
                    // 기본월 확정 후 사용량 파라미터가 없었던 경우 보충 로드
                    await this.ensureInsightEnergy(this.energy);
                } else {
                    this.putUsageCache(this.energy, null);
                    this.usageEmpty = true;
                    this.usageData = null;
                }
                if (!this.isAllEnergy) {
                    this.syncInsightDisplay(this.energy);
                }
                await this.ensureSummaryForecast();
                this.historyLoaded = false;
                if (this.activeTab === "history") {
                    this.loadHistory(true);
                }
                this.scheduleInsightPrefetch();
            } catch (e) {
                this.billingByEnergy = {};
                this.clearInsightCaches();
                this.compare = null;
                this.forecast = null;
                this.usageEmpty = true;
                this.usageData = null;
                this.showErrorToast(this.apiErrorMessage(e, "에너지 정보를 불러오지 못했습니다."));
            } finally {
                this.endUsagePending();
            }
        },
        async reloadEnergyData() {
            await this.loadDashboard();
        },
        async loadCompare() {
            try {
                const { data } = await backEndApi.mobileResident.getCompare({
                    regYm: this.regYm,
                    energySource: this.energy,
                });
                this.putCompareCache(this.energy, data);
                if (data?.amountTrail && this.isAllEnergy) {
                    this.amountTrail = data.amountTrail;
                }
                if (this.hasInsightCache(this.energy) || this.hasCompareCache(this.energy)) {
                    this.compare = data;
                }
            } catch (e) {
                if (!this.hasCompareCache(this.energy)) {
                    this.compare = null;
                }
                this.showErrorToast(this.apiErrorMessage(e, "비교 정보를 불러오지 못했습니다."));
            }
        },
        async loadUsage() {
            if (!this.me) return;
            if (this.hasUsageCache(this.energy)) {
                this.syncInsightDisplay(this.energy);
                return;
            }
            this.beginUsagePending();
            try {
                const { data } = await backEndApi.mobileResident.getUsage(this.buildUsageParams());
                this.applyUsageData(data, this.energy);
            } catch (e) {
                if (!this.hasUsageCache(this.energy)) {
                    this.usageEmpty = true;
                    this.usageData = null;
                }
                this.showErrorToast(this.apiErrorMessage(e, "사용량 추이를 불러오지 못했습니다."));
            } finally {
                this.endUsagePending();
            }
        },
        getChartSeries() {
            const src = this.findUsageSource();
            const unit = src?.unit || "kWh";
            const rows = src?.series || [];
            if (this.period === "month") {
                return {
                    labels: rows.map((r) => r.label || this.formatMonthLabel(r.regDt)),
                    tooltipLabels: rows.map((r) => r.label || this.formatMonthTooltip(r.regDt)),
                    values: rows.map((r) => r.value),
                    unit,
                };
            }
            return {
                labels: rows.map((r) => this.formatDayAxisLabel(r.regDt)),
                tooltipLabels: rows.map((r) => this.formatDayTooltip(r.regDt)),
                values: rows.map((r) => r.value),
                unit,
            };
        },
        formatMonthLabel(regDt) {
            const s = String(regDt || "").replace(/\D/g, "");
            if (s.length >= 6) return `${parseInt(s.slice(4, 6), 10)}월`;
            return s;
        },
        formatMonthTooltip(regDt) {
            const s = String(regDt || "").replace(/\D/g, "");
            if (s.length >= 6) return `${parseInt(s.slice(4, 6), 10)}월`;
            return s || "-";
        },
        formatDayAxisLabel(regDt) {
            const s = String(regDt || "").replace(/\D/g, "");
            if (s.length < 8) return "";
            const day = parseInt(s.slice(6, 8), 10);
            return day === 1 || day % 5 === 0 || day === 31 ? String(day) : "";
        },
        formatDayTooltip(regDt) {
            const s = String(regDt || "").replace(/\D/g, "");
            if (s.length >= 8) return `${s.slice(4, 6)}-${s.slice(6, 8)}`;
            if (s.length >= 6) return `${s.slice(4, 6)}-??`;
            return "-";
        },
        /** 인사이트 요약용 라벨 — 일별은 축 라벨("07-15") 대신 "7월 15일" */
        insightPointLabel(series, i) {
            if (this.period !== "day") return series.tooltipLabels[i] || series.labels[i];
            const [mm, dd] = String(series.tooltipLabels[i] || "").split("-");
            if (!mm || !dd) return series.tooltipLabels[i] || "";
            return `${parseInt(mm, 10)}월 ${parseInt(dd, 10)}일`;
        },
        logout() {
            // 라우팅은 App.vue isAuthenticated(false) watcher 가 담당 — 이중 push/replace 방지
            this.$store.dispatch("LOGOUT", this.$store.getters.loginUser);
        },
    },
};
</script>

<style scoped>
/*
 * 관리비 고지서 문법 — 그림자로 층을 쌓지 않고 선(rule)으로 구획한다.
 *
 * 활자 6단계
 *   0.88rem(14px) 캡션·각주 / 1rem(16px) 본문·레이블 / 1.06rem(17px) 표 값
 *   1.19rem(19px) 블록 표제 / 1.38rem(22px) 섹션 표제 / 2.5rem(40px) 총 합계
 * 회색 2단계
 *   #4b5363(약 7.3:1) / #6b7484(약 4.5:1). 그보다 옅은 회색은 WCAG AA 미달.
 *   회색·착색 배경 위에서는 #6b7484 가 4.5:1 에 못 미쳐 #4b5363 을 쓴다.
 */

/* 문서 표제 — 세대 정보 */
.ctx-bar {
    position: sticky;
    /* MobileAppShell --shell-header-height (safe-area 재가산 금지) */
    top: var(--shell-header-height, 48px);
    z-index: 40;
    margin: -12px -16px 14px;
    padding: 12px 16px 10px;
    background: #f0f2f5;
}
.ctx-bar .hh-row {
    margin-bottom: 10px;
}
.hh-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 14px;
}
/* 문서 표제 — 섹션 표제(1.38rem)와 같으면 위계가 성립하지 않아 한 단계 위로 둔다 */
.hh-name {
    font-size: 1.5rem;
    font-weight: 800;
    color: #1a1f27;
    letter-spacing: -0.01em;
}
.hh-sub {
    margin-top: 3px;
    font-size: 0.88rem;
    font-weight: 600;
    color: #4b5363;
}
.hh-path {
    font-size: 0.88rem;
    /* 흰 카드가 아니라 페이지 배경(#f0f2f5) 위라 한 단계 진한 톤을 쓴다 */
    color: #4b5363;
    margin-top: 3px;
}
.hh-month {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: 1rem;
    font-weight: 700;
    color: #4b5363;
    background: #fff;
    border: 1px solid #d7dbe3;
    padding: 8px 13px;
    border-radius: 8px;
    max-width: 58%;
    cursor: pointer;
    transition:
        border-color 0.18s,
        box-shadow 0.18s;
}
.hh-month-label {
    font-size: 0.94rem;
    font-weight: 700;
    color: #1a1f27;
    line-height: 1.2;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.hh-month:active {
    border-color: #2d5fc7;
    box-shadow: 0 0 0 3px rgba(45, 95, 199, 0.12);
}
.hh-month-icon {
    font-size: 0.81rem;
    color: #6b7484;
    flex-shrink: 0;
}

/* 월 선택 sheet */
.sheet-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(20, 30, 60, 0.35);
    z-index: 200;
}
.sheet-panel {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 201;
    display: flex;
    flex-direction: column;
    background: #fff;
    border-radius: 18px 18px 0 0;
    padding: 12px 18px 0;
    max-height: 50vh;
    max-height: 50dvh;
    transform: translateY(100%);
    transition: transform 0.28s ease;
    box-shadow: 0 -8px 32px rgba(20, 30, 60, 0.12);
}
.sheet-panel.open {
    transform: translateY(0);
}
.sheet-handle {
    width: 36px;
    height: 4px;
    background: #e6e9ef;
    border-radius: 99px;
    margin: 0 auto 14px;
    flex-shrink: 0;
}
.sheet-title {
    font-size: 1.19rem;
    font-weight: 800;
    color: #1a1f27;
    margin-bottom: 12px;
    flex-shrink: 0;
}
.sheet-panel > .state-box {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding-bottom: calc(18px + env(safe-area-inset-bottom, 0px));
}
.sheet-list {
    display: flex;
    flex-direction: column;
    gap: 6px;
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    padding-bottom: calc(18px + env(safe-area-inset-bottom, 0px));
}
.sheet-item {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    padding: 12px 14px;
    border: 1px solid #eef1f6;
    border-radius: 10px;
    background: #fff;
    text-align: left;
    cursor: pointer;
    transition:
        background 0.15s,
        border-color 0.15s;
}
.sheet-item.active {
    background: #f5f8ff;
    border-color: #2d5fc7;
}
.sheet-item-main {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
}
.sheet-item-ym {
    font-size: 1.12rem;
    font-weight: 700;
    color: #1a1f27;
}
.sheet-item-period {
    font-size: 0.81rem;
    font-weight: 600;
    color: #6b7484;
}
.sheet-item-amt {
    font-size: 1.06rem;
    font-weight: 800;
    color: #4b5363;
    flex-shrink: 0;
}
.sheet-item-pending {
    font-size: 0.81rem;
    font-weight: 600;
    color: #4b5363;
    flex-shrink: 0;
    max-width: 42%;
    text-align: right;
}
.sheet-item-check {
    color: #2d5fc7;
    font-size: 1rem;
}

/* 공통 카드 — 고지서의 한 구획. 그림자 대신 테두리로만 경계를 만든다 */
.card {
    border-radius: 14px;
    padding: 20px 18px;
    margin-bottom: 14px;
    background: #fff;
    border: 1px solid #e2e6ed;
}

/* 섹션 표제 — 카드마다 규격이 달라 제목이 제목처럼 안 보였던 문제를 하나로 묶는다 */
.sec-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    /* 320px 기기에서는 제목+우측요소가 카드 폭을 넘어 두 줄로 흘려보낸다 */
    flex-wrap: wrap;
    gap: 8px 10px;
    margin-bottom: 16px;
}
.sec-head > .period-toggle,
.sec-head > .hero-delta {
    margin-left: auto;
}
.sec-title {
    margin: 0;
    font-size: 1.38rem;
    font-weight: 800;
    color: #1a1f27;
    letter-spacing: -0.01em;
}
/* 청구 요약은 공용 에너지원 선택과 무관함을 짧게 밝힌다 */
.sec-cap {
    margin-left: auto;
    font-size: 0.88rem;
    font-weight: 600;
    color: #6b7484;
}
/* 블록 표제 — 섹션 표제보다 한 단계 아래. 예전엔 크기가 같아 위계가 무너져 있었다 */
.blk-title {
    margin: 0;
    font-size: 1.19rem;
    font-weight: 700;
    color: #1a1f27;
}

/* 숫자 — 본문 폰트를 그대로 쓰고, 폭 고정 숫자(tabular)로 자릿수만 정렬 */
.num {
    font-variant-numeric: tabular-nums;
    font-feature-settings: "tnum" 1;
}

/* 청구 요약표 — 고지서 1면. 항목 나열 후 이중선 아래 총 합계 */
.sum-table {
    display: flex;
    flex-direction: column;
}
.sum-row {
    display: grid;
    grid-template-columns: 52px 1fr auto;
    gap: 12px;
    align-items: baseline;
    padding: 11px 0;
    border-bottom: 1px dashed #e2e6ed;
}
/* 합계 이중선 바로 위 점선은 겹쳐 보이므로 마지막 행만 제거 */
.sum-row:last-child {
    border-bottom: none;
}
.sum-name {
    font-size: 1rem;
    font-weight: 700;
    color: #1a1f27;
}
.sum-usage {
    text-align: right;
    font-size: 1rem;
    color: #6b7484;
}
.sum-amt {
    text-align: right;
    font-size: 1.06rem;
    font-weight: 700;
    color: #1a1f27;
    min-width: 84px;
}
.sum-amt.is-pending {
    font-weight: 600;
    color: #6b7484;
}
.sum-amt.is-forecast,
.bill-val.is-forecast,
.all-en-amt.is-forecast {
    color: #2d5fc7;
}
.forecast-note {
    display: flex;
    align-items: flex-start;
    gap: 7px;
    margin: 12px 0 0;
    font-size: 0.88rem;
    line-height: 1.45;
    color: #4b5363;
}
.forecast-note i {
    margin-top: 2px;
    color: #6b7484;
}
.forecast-note.is-shock {
    color: #8a5a00;
}
.forecast-note.is-shock i {
    color: #b7791f;
}
.forecast-card.is-shock {
    border-color: #f0d9a8;
    background: #fffbf3;
}
.forecast-card .forecast-hero {
    padding: 4px 0 0;
}
.forecast-amt {
    font-size: 2.25rem;
    font-weight: 800;
    color: #2d5fc7;
    letter-spacing: -0.02em;
    line-height: 1.1;
}
.forecast-amt small {
    margin-left: 3px;
    font-size: 1.1rem;
    font-weight: 700;
    color: #4b5363;
}
.forecast-meta {
    margin-top: 10px;
    font-size: 0.88rem;
    font-weight: 600;
    color: #6b7484;
}
.all-energy-list {
    display: flex;
    flex-direction: column;
}
.all-energy-row {
    display: grid;
    grid-template-columns: 48px 1fr auto;
    gap: 10px;
    align-items: baseline;
    padding: 12px 0;
    border-bottom: 1px dashed #e2e6ed;
    font-size: 1rem;
}
.all-energy-row:last-of-type {
    border-bottom: none;
}
.all-en-name {
    font-weight: 700;
    color: #4b5363;
}
.all-en-usage {
    text-align: right;
    color: #6b7484;
}
.all-en-amt {
    font-weight: 700;
    color: #1a1f27;
}
/* 총 합계 — 헤더 블루를 옅게 한 solid 구분선 */
.sum-total {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 10px;
    margin-top: 4px;
    padding-top: 14px;
    border-top: 2px solid #9bb6e0;
}
.sum-total-label {
    font-size: 1.19rem;
    font-weight: 800;
    color: #1a1f27;
}
.sum-total-val {
    font-size: 2.5rem;
    font-weight: 800;
    color: #1a1f27;
    letter-spacing: -0.02em;
    line-height: 1;
}
.sum-total-val small {
    margin-left: 3px;
    font-size: 1.19rem;
    font-weight: 700;
    color: #4b5363;
}

/* 상세 대상 선택 — 상세 카드 안에 있어 무엇을 바꾸는 컨트롤인지 자명하다 */
.seg-row {
    display: flex;
    gap: 7px;
    margin-bottom: 16px;
}
.seg-row-ctx {
    margin-bottom: 0;
}
.seg-btn {
    flex: 1;
    padding: 11px 0;
    border: 1px solid #d7dbe3;
    background: #fff;
    color: #4b5363;
    border-radius: 8px;
    font-size: 1rem;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.18s;
}
.seg-btn.active {
    background: #2d5fc7;
    border-color: #2d5fc7;
    color: #fff;
}

/* 청구 상세 */
.hero-delta {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 0.88rem;
    font-weight: 700;
    padding: 5px 10px;
    border-radius: 6px;
    white-space: nowrap;
}
.hero-delta.is-up {
    color: #c0392b;
    background: #fdecec;
}
.hero-delta.is-down {
    color: #1e7e52;
    background: #eaf7ef;
}
/* 항목명과 값을 좌우로 맞춘 고지서 행 */
.bill-lines {
    display: flex;
    flex-direction: column;
    margin-bottom: 14px;
}
.bill-line {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 10px;
    padding: 10px 0;
    border-bottom: 1px dashed #e2e6ed;
}
.bill-line:last-child {
    border-bottom: none;
}
.bill-key {
    font-size: 1rem;
    font-weight: 600;
    color: #6b7484;
}
.bill-val {
    font-size: 1.06rem;
    font-weight: 700;
    color: #1a1f27;
}
.bill-val small {
    margin-left: 2px;
    font-size: 1rem;
    font-weight: 600;
    color: #4b5363;
}
/* 청구 금액은 요약표 합계(40px)보다 한 단계 아래에 둬 위계를 지킨다 */
.bill-line.is-major .bill-val {
    font-size: 2rem;
    font-weight: 800;
    letter-spacing: -0.02em;
    line-height: 1.1;
}
.bill-line.is-major .bill-val small {
    font-size: 1.06rem;
    font-weight: 700;
}
.bill-val.is-pending {
    font-size: 1.19rem;
    font-weight: 700;
    color: #6b7484;
}

/* 부가 정보 — 검침 기간과 확정일을 한 줄로 합치고 옅게 */
.hero-meta {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 0.88rem;
    color: #6b7484;
}
.hero-meta:last-child {
    margin-bottom: 0;
}

/* 산출 내역 — 영수증형 박스 */
.hero-receipt {
    background: #f9fafb;
    border: 1px solid #e2e6ed;
    border-radius: 10px;
    padding: 14px 16px;
    display: flex;
    flex-direction: column;
    gap: 10px;
}
.hero-receipt .blk-title {
    font-size: 1rem;
    font-weight: 700;
    color: #4b5363;
}
.detail-row {
    display: flex;
    justify-content: space-between;
    gap: 10px;
    font-size: 1rem;
}
.detail-row > span:first-child {
    color: #6b7484;
}
.detail-row .num {
    color: #1a1f27;
    font-weight: 600;
}
.detail-row .num.is-up {
    color: #c0392b;
}
.detail-row .num.is-down {
    color: #1e7e52;
}
/* 합계 — 요약표와 같은 이중선 관용 표현 */
.detail-total {
    padding-top: 11px;
    border-top: 3px double #b6bcc7;
}
.detail-total > span:first-child {
    font-size: 1.06rem;
    color: #1a1f27;
    font-weight: 700;
}
.detail-total .num {
    font-size: 1.19rem;
    font-weight: 800;
}

/* 추이 */
/* 보기 옵션 — 전폭으로 두면 에너지 선택기와 같은 급의 컨트롤로 오해된다 */
.period-toggle {
    display: flex;
    flex: 0 0 auto;
    background: #f0f2f7;
    border-radius: 9px;
    padding: 2px;
}
.period-toggle button {
    padding: 7px 13px;
    border: none;
    background: transparent;
    border-radius: 7px;
    font-size: 0.88rem;
    font-weight: 700;
    /* 회색 트랙(#f0f2f7) 위라 한 단계 진한 톤을 쓴다 */
    color: #4b5363;
    cursor: pointer;
}
.period-toggle button.active {
    background: #fff;
    color: #2d5fc7;
    box-shadow: 0 1px 3px rgba(20, 30, 60, 0.12);
}
/* 사용 분석 인사이트 */
.ins-wrap {
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 18px;
    margin-top: 14px;
}
.ins-msg {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 16px;
    background: #f8fafe;
    border-radius: 10px;
    border: 1px solid #e2e6ed;
}
.ins-msg.is-save {
    background: #f4fbf7;
    border-color: #dcefe4;
}
.ins-msg.is-over {
    background: #fdf6f5;
    border-color: #f5dedb;
}
.ins-msg.is-mute {
    background: #fafbfd;
    border-color: #eef1f6;
}
.ins-icon {
    flex: 0 0 auto;
    width: 46px;
    height: 46px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.38rem;
    border-radius: 10px;
    background: #eef3fd;
    color: #2d5fc7;
}
.ins-icon.is-save {
    background: #eaf7ef;
    color: #1e7e52;
}
.ins-icon.is-over {
    background: #fdecec;
    color: #c0392b;
}
.ins-icon.is-mute {
    background: #f1f3f7;
    color: #6b7484;
}
.ins-text strong {
    display: block;
    font-size: 1.19rem;
    font-weight: 800;
    color: #1a1f27;
    margin-bottom: 4px;
}
.ins-text p {
    margin: 0;
    font-size: 1rem;
    /* 착색 배경(#f4fbf7·#fdf6f5) 위에 올라가므로 한 단계 진한 톤을 쓴다 */
    color: #4b5363;
    line-height: 1.45;
}

/* 게이지 — 내 평균을 50% 로 두고, 구간 폭(40/20/40)과 라벨 폭을 일치시킨다 */
.ins-gauge-labels {
    display: flex;
    font-size: 0.88rem;
    font-weight: 700;
    color: #6b7484;
    margin-bottom: 6px;
}
.z-save {
    flex: 0 0 40%;
    text-align: left;
}
.z-mid {
    flex: 0 0 20%;
    text-align: center;
}
.z-over {
    flex: 0 0 40%;
    text-align: right;
}
.ins-gauge-track {
    position: relative;
    height: 10px;
    border-radius: 99px;
    background: linear-gradient(to right, #eaf7ef 0 40%, #eef1f6 40% 60%, #fdecec 60% 100%);
}
.ins-gauge-pin {
    position: absolute;
    top: -4px;
    width: 4px;
    height: 18px;
    border-radius: 2px;
    background: #2d5fc7;
    transform: translateX(-50%);
    transition: left 0.6s cubic-bezier(0.4, 0, 0.2, 1);
}
.ins-gauge-pin.is-save {
    background: #1e7e52;
}
.ins-gauge-pin.is-over {
    background: #c0392b;
}
.ins-gauge-caption {
    margin-top: 6px;
    font-size: 0.88rem;
    color: #6b7484;
    text-align: right;
}

.ins-list {
    display: flex;
    flex-direction: column;
}
.ins-row {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 10px;
    padding: 11px 0;
    border-bottom: 1px dashed #e2e6ed;
}
.ins-row:last-child {
    border-bottom: none;
}
.ins-label {
    font-size: 1rem;
    color: #6b7484;
    font-weight: 600;
}
.ins-value-stack {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 2px;
    text-align: right;
}
.ins-value {
    font-size: 1.06rem;
    font-weight: 700;
    color: #1a1f27;
}
.ins-value.is-over {
    color: #c0392b;
}
.ins-value.is-save {
    color: #1e7e52;
}
.ins-value-sub {
    font-size: 0.88rem;
    font-weight: 600;
    color: #6b7484;
}
/* 카드 푸터 — 본문이 끝났음을 구분선으로 알리고 부가 정보를 묶는다 */
.ins-foot {
    padding-top: 14px;
    border-top: 1px solid #e2e6ed;
}
.ins-note {
    display: flex;
    align-items: flex-start;
    gap: 7px;
    margin: 0;
    font-size: 0.88rem;
    line-height: 1.5;
    color: #4b5363;
}
.ins-note i {
    margin-top: 3px;
    color: #2d5fc7;
}
/* 기간 표기 — 표 캡션처럼 헤더 바로 아래에서 집계 범위를 먼저 알려준다 */
.usage-period {
    margin: 0;
    font-size: 0.88rem;
    font-weight: 600;
    color: #6b7484;
}
.ins-skel-slot {
    min-height: 200px;
    margin-top: 14px;
}
.ins-skel {
    display: flex;
    flex-direction: column;
    gap: 18px;
    margin-top: 14px;
}
.ins-skel-msg {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 16px;
    background: #f8fafe;
    border-radius: 10px;
    border: 1px solid #e2e6ed;
}
.ins-skel-icon {
    flex: 0 0 auto;
    width: 46px;
    height: 46px;
    border-radius: 50%;
}
.ins-skel-text {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 8px;
}
.ins-skel-gauge {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 0 4px;
}
.ins-skel-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
}
.ins-skel-row {
    display: flex;
    justify-content: space-between;
    gap: 12px;
}
.ins-skel-line {
    display: block;
    height: 12px;
    border-radius: 6px;
    background: #e8ecf2;
}
.ins-skel-line.h-8 {
    height: 8px;
}
.ins-skel-line.w-36 {
    width: 36%;
}
.ins-skel-line.w-40 {
    width: 40%;
}
.ins-skel-line.w-44 {
    width: 44%;
}
.ins-skel-line.w-72 {
    width: 72%;
}
.ins-skel-line.w-90 {
    width: 90%;
}
.ins-skel-line.w-100 {
    width: 100%;
}
.ins-skel-line.center {
    align-self: center;
}
.skel-shimmer {
    position: relative;
    overflow: hidden;
    background: #e8ecf2;
}
.skel-shimmer::after {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.55), transparent);
    animation: ins-skel-sweep 1.4s ease-in-out infinite;
}
@keyframes ins-skel-sweep {
    0% {
        transform: translateX(-100%);
    }
    100% {
        transform: translateX(100%);
    }
}
@media (prefers-reduced-motion: reduce) {
    .skel-shimmer::after {
        animation: none;
    }
}

/* 비교 — 원장형 테이블 */
/* 섹션 결론 — 근거 수치(v-delta)보다 무게가 가벼우면 위계가 뒤집힌다 */
.cmp-insight {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 13px 14px;
    border: 1px solid #e2e6ed;
    border-radius: 10px;
    background: #f8fafe;
    margin-bottom: 14px;
}
.cmp-insight i {
    flex: 0 0 auto;
    font-size: 1.19rem;
    color: #2d5fc7;
}
.cmp-insight strong {
    display: block;
    font-size: 1.19rem;
    font-weight: 800;
    color: #1a1f27;
}
.cmp-insight p {
    margin: 3px 0 0;
    font-size: 0.88rem;
    line-height: 1.4;
    /* 착색 배경(#f4fbf7·#fdf6f5) 위에 올라가므로 한 단계 진한 톤을 쓴다 */
    color: #4b5363;
}
.cmp-insight.is-save {
    background: #f4fbf7;
    border-color: #dcefe4;
}
.cmp-insight.is-save i,
.cmp-insight.is-save .cmp-key {
    color: #1e7e52;
}
.cmp-insight.is-over {
    background: #fdf6f5;
    border-color: #f5dedb;
}
.cmp-insight.is-over i,
.cmp-insight.is-over .cmp-key {
    color: #c0392b;
}
.cmp-block {
    background: #f9fafb;
    border: 1px solid #e2e6ed;
    border-radius: 10px;
    padding: 6px 16px 14px;
    margin-bottom: 10px;
}
.cmp-block:last-child {
    margin-bottom: 0;
}
.grp-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 12px 0 8px;
}
.grp-meta {
    font-size: 0.88rem;
    color: #6b7484;
}
.ledger-row {
    display: grid;
    grid-template-columns: 62px 1fr auto;
    gap: 8px;
    align-items: baseline;
}
.ledger-head {
    align-items: center;
    font-size: 0.88rem;
    font-weight: 700;
    color: #6b7484;
    padding-bottom: 7px;
    border-bottom: 1px solid #d7dbe3;
}
.ledger-data {
    padding-top: 12px;
}
.ledger-gap {
    padding-top: 10px;
}
.ledger-row > .r {
    text-align: right;
}
.ledger-row > .delta {
    text-align: right;
    min-width: 72px;
}
.metric-label {
    font-size: 1rem;
    font-weight: 600;
    color: #6b7484;
}
.v-base {
    font-size: 1.06rem;
    font-weight: 600;
    color: #4b5363;
}
.v-delta {
    font-size: 1.06rem;
    font-weight: 700;
}
.v-delta.is-up {
    color: #c0392b;
}
.v-delta.is-down {
    color: #1e7e52;
}
.v-delta.is-neutral {
    color: #6b7484;
}
/* 섹션 푸터 — 인사이트 카드의 ins-foot 과 같은 리듬을 쓴다 */
.cmp-foot {
    display: flex;
    align-items: flex-start;
    gap: 7px;
    margin-top: 14px;
    padding-top: 14px;
    border-top: 1px solid #e2e6ed;
    font-size: 0.88rem;
    line-height: 1.5;
    color: #4b5363;
}
.cmp-foot i {
    margin-top: 3px;
    color: #2d5fc7;
}
.cmp-foot p {
    margin: 0;
}
.cmp-foot p + p {
    margin-top: 4px;
}

/* 상태 */
.state-box {
    /* 미지정 시 전역 body(0.75rem = 12px)를 상속해 유독 작아진다 */
    font-size: 1rem;
    text-align: center;
    padding: 48px 16px;
    color: #6b7484;
}
.state-box.small {
    padding: 24px;
}
.state-box.error {
    color: #c0392b;
}

.empty-state i {
    display: block;
    margin-bottom: 10px;
    color: #6b7484;
}
.empty-state.error i {
    color: #c0392b;
}
.empty-title {
    margin: 0;
    font-size: 1.06rem;
    font-weight: 700;
    color: #4b5363;
}
.empty-desc {
    margin: 6px 0 0;
    font-size: 0.88rem;
    line-height: 1.45;
    color: #6b7484;
}
.empty-state.inline {
    text-align: center;
    padding: 18px 8px 8px;
}
.retry-btn {
    margin-top: 14px;
    padding: 10px 18px;
    border: 1px solid #d7dbe3;
    border-radius: 8px;
    background: #fff;
    color: #2e579c;
    font-size: 1rem;
    font-weight: 700;
    cursor: pointer;
}

/* 청구 이력 */
.hist-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
}
.hist-card {
    background: #fff;
    border: 1px solid #e2e6ed;
    border-radius: 14px;
    overflow: hidden;
}
.hist-toggle {
    display: block;
    width: 100%;
    padding: 16px 16px 12px;
    border: none;
    background: transparent;
    text-align: left;
    cursor: pointer;
}
.hist-main {
    display: flex;
    align-items: center;
    gap: 12px;
}
.hist-ym {
    flex: 1;
    min-width: 0;
    font-size: 1.19rem;
    font-weight: 800;
    color: #1a1f27;
}
.hist-amt {
    flex-shrink: 0;
    font-size: 1.19rem;
    font-weight: 800;
    color: #1a1f27;
}
.hist-chevron {
    flex-shrink: 0;
    width: 1rem;
    text-align: center;
    color: #6b7484;
    font-size: 0.88rem;
}
.hist-meta {
    margin-top: 8px;
    padding-right: calc(1rem + 12px); /* chevron 열과 정렬 */
}
.hist-yoy {
    font-size: 0.88rem;
    font-weight: 700;
}
.hist-yoy.is-up {
    color: #c0392b;
}
.hist-yoy.is-down {
    color: #1e7e52;
}
.hist-yoy.is-neutral {
    color: #6b7484;
    font-weight: 600;
}
.hist-detail {
    padding: 0 16px 16px;
    border-top: 1px dashed #e2e6ed;
}
.hist-energy {
    display: grid;
    grid-template-columns: 48px 1fr auto;
    gap: 10px;
    align-items: baseline;
    padding: 11px 0;
    border-bottom: 1px dashed #eef1f6;
    font-size: 1rem;
}
.hist-energy:last-of-type {
    border-bottom: none;
}
.hist-en-name {
    font-weight: 700;
    color: #4b5363;
}
.hist-en-usage {
    text-align: right;
    color: #6b7484;
}
.hist-en-amt {
    font-weight: 700;
    color: #1a1f27;
}

/* 더보기 */
.more-rows {
    display: flex;
    flex-direction: column;
}
.more-row {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    padding: 12px 0;
    border-bottom: 1px dashed #e2e6ed;
    font-size: 1rem;
}
.more-row:last-child {
    border-bottom: none;
}
.more-key {
    color: #6b7484;
    font-weight: 600;
}
.more-val {
    color: #1a1f27;
    font-weight: 700;
    text-align: right;
}
.more-action {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 100%;
    padding: 14px;
    border: 1px solid #e2e6ed;
    border-radius: 10px;
    background: #fff;
    color: #1a1f27;
    font-size: 1.06rem;
    font-weight: 700;
    cursor: pointer;
}
.more-actions {
    display: flex;
    flex-direction: column;
    gap: 10px;
}
.more-action.danger {
    color: #c0392b;
    border-color: #f0d0cc;
    background: #fdf8f7;
}
</style>
